import PaymentStatusFilter from '../../Common/components/PaymentStatusFilter';
import PaymentResultMonitor from '../../Common/components/PaymentResultMonitor';
import { showUnknownPayment, isUnknownPayment, unknownPaymentLabel, safePaymentResultMessage, paymentResultFields } from '../../Common/utils/paymentResult';
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import Calendar from 'react-calendar';
import dayjs from 'dayjs';
import { Banknote, CreditCard, Download, Eye, Search, HelpCircle } from 'lucide-react';
import { Tooltip } from 'react-tooltip';
import axios from 'axios';
import dayjsUtc from 'dayjs/plugin/utc';
import { useToast } from '../../Common/context/ToastContext';
import { useTooltip } from '../../Common/context/TooltipContext';
import ReceiptPreviewModalV2 from '../../Common/components/receipt/ReceiptPreviewModalV2';
import { resolveUnmeReceiptInfo, receiptSendError } from '../../Common/api/unmeReceiptInfo';
import useCurrentReceiptEmail from '../../Common/hooks/useCurrentReceiptEmail';
import CurrentReceiptEmailStatus from '../../Common/components/receipt/CurrentReceiptEmailStatus';
import ReceiptImageActions from '../../Common/components/receipt/ReceiptImageActions';
import { RECEIPT_UI_VERSION } from '../../Common/config/receiptUi';
import { RECEIPT_CARD_SMS_NOTICE, RECEIPT_LABELS } from '../../Common/constants/receiptLabels';
import { BRAND_COPYRIGHT } from '../../Common/config/brandConfig';
import { downloadBlob } from '../../Common/api/client';
import { checkLogin, setOtpVerified } from '../../Common/store';
import { cpMe } from '../api/cpAuth';
import { fetchManualPaymentExportRows } from '../api/manualPaymentExport';
import { useContractBreakdownAvailability, BreakdownToggle, ContractBreakdown } from '../components/ContractBreakdown';
import { transactionContractLabel } from '../utils/contractDisplay';
import { isUnmePaymentRecovery, withUnmePaymentRecoveryParams } from '../../Common/utils/unmePaymentRecovery';

dayjs.extend(dayjsUtc);

const baseUrl = process.env.REACT_APP_API || '';
const RECEIPT_SMS_ENABLED = Number(process.env.REACT_APP_RECEIPT_SMS_ENABLED ?? 0) === 1;
const RECEIPT_KAKAO_ENABLED = Number(process.env.REACT_APP_RECEIPT_KAKAO_ENABLED ?? 0) === 1;
const PHPAY_INTEGRATED_SETTLEMENT_EXPORT_ENABLED =
  String(process.env.REACT_APP_BRAND_VARIANT || '').trim().toLowerCase() === 'phpay';
const TRANSACTION_STATUS_LABELS = {
  COMPLETED: '승인',
  CANCELED: '취소',
  FAILED: '실패',
  REQUESTED: '요청 중',
  APPROVED: '승인 처리 중',
};

/** 전표 공급자 정보 (통합정산 전표용) */
const RECEIPT_SUPPLIER_INFO = {
  supplierName: '-',
  supplierOwnerName: '-',
  supplierBusinessNo: '-',
  supplierContact: '-',
  supplierAddress: '-',
};

/** 전표 카드 가맹점 정보 (통합정산 전표용) */
const RECEIPT_CARD_MERCHANT_INFO = {
  cardMerchantName: '-',
  cardMerchantOwnerName: '-',
  cardMerchantBusinessNo: '-',
  cardMerchantContact: '-',
  cardMerchantAddress: '-',
};

function formatKST(dateStr, format = 'YYYY.MM.DD HH:mm') {
  if (!dateStr) return '-';
  return dayjs.utc(dateStr).add(9, 'hour').format(format);
}

function parseCardNameFromRaw(pgRawResponse) {
  if (!pgRawResponse || typeof pgRawResponse !== 'string') return '-';
  try {
    const parsed = JSON.parse(pgRawResponse);
    const data = parsed?.data;
    return data?.cardNm || data?.cardKind || '-';
  } catch (_) {
    return '-';
  }
}

function getCanceledFeeRefundAmount(item) {
  return Number(item?.canceled_fee_refund_amount ?? item?.canceledFeeRefundAmount ?? 0);
}

function getSettlementFeeAmount(item) {
  const feeAmount = Number(
    item?.settlement_fee_amount ??
    item?.settlementFeeAmount ??
    item?.fee_amount ??
    item?.feeAmount ??
    item?.cp_fee_amount ??
    item?.cpFeeAmount ??
    item?.fee_amt ??
    item?.feeAmt ??
    item?.fee ??
    0
  );
  return feeAmount - getCanceledFeeRefundAmount(item);
}

function getSettlementStage(status) {
  const normalized = String(status || '').toUpperCase();
  if (normalized === 'PENDING') return 'pending';
  if (normalized === 'TRANSFERRED') return 'transferred';
  return 'confirmed';
}

function getSettlementStageLabel(status) {
  const stage = getSettlementStage(status);
  if (stage === 'pending') return '정산예정';
  if (stage === 'transferred') return '입금완료';
  return '정산확정';
}

function mapPaymentToTransaction(item) {
  const paymentTransactionId =
    item.payment_transaction_id ??
    item.paymentTransactionId ??
    item.id ??
    item.paymentId ??
    item.payment_id;
  const id =
    item.transaction_id ??
    item.transactionId ??
    [
      paymentTransactionId ?? 'payment',
      item.transaction_type ?? item.transactionType ?? 'UNKNOWN',
      item.business_created_at ?? item.businessCreatedAt ?? '',
    ].join(':');
  const businessDate =
    item.business_created_at ??
    item.businessCreatedAt;
  const dateStr =
    businessDate ??
    item.approved_at ??
    item.approvedAt ??
    item.approval_date ??
    item.approvalDate ??
    item.created_at ??
    item.createdAt ??
    item.date;
  const date = dateStr
    ? businessDate
      ? dayjs.utc(dateStr).subtract(9, 'hour').toISOString()
      : dayjs.utc(dateStr).toISOString()
    : dayjs().toISOString();
  const instmtMon =
    item.instmt_mon ??
    item.instmtMon ??
    item.pg_instmt_mon ??
    item.installmentMonths ??
    item.installment_months ??
    '00';
  const type = instmtMon === '00' ? '일시불' : `${instmtMon}개월`;
  const cardName =
    item.card_nm ??
    item.cardNm ??
    item.cardName ??
    parseCardNameFromRaw(item.pg_raw_response) ??
      '-';
  const status = item.status || '';
  const transactionType = String(
    item.transaction_type ?? item.transactionType ?? ''
  ).toUpperCase();
  const transactionStatus = isUnknownPayment(item) ? 'UNKNOWN' : item.transaction_status ?? item.transactionStatus ?? '';
  const rawAmount = Number(
    item.amount ??
    item.trdAmt ??
    item.trd_amt ??
    item.pg_trd_amt ??
    item.request_trd_amt ??
    0
  );
  const rawFee = Number(
    item.cp_fee_amount ??
    item.cpFeeAmount ??
    item.fee_amt ??
    item.feeAmt ??
    item.fee_amount ??
    item.fee ??
    0
  );
  const payoutDate =
    item.transfer_date ??
    item.transferDate ??
    item.deposit_date ??
    item.depositDate ??
    item.payout_date ??
    item.payoutDate ??
    item.settlement_date ??
    item.settlementDate ??
    '';

  return {
    ...paymentResultFields(item),
    id,
    customerEmailTransactionId: item.transaction_id,
    paymentTransactionId,
    receiptTransactionId: item.transaction_id ?? item.transactionId ?? item.payment_transaction_id ?? item.paymentTransactionId,
    paymentId: paymentTransactionId,
    date,
    pharmacy:
      item.transaction_pharmacy_name ??
      item.transactionPharmacyName ??
      item.pharmacy_name ??
      item.pharmacyName ??
      item.mcht_cust_nm ??
      item.mchtCustNm ??
      item.pharmacy ??
      item.customer_name ??
      item.customerName ??
      '-',
    amount: transactionType === 'CANCEL' ? -Math.abs(rawAmount) : rawAmount,
    approvalNo:
      item.appr_no ??
      item.apprNo ??
      item.approval_no ??
      item.approvalNumber ??
      '',
    fee: transactionType === 'CANCEL' ? -Math.abs(rawFee) : rawFee,
    driver:
      item.salesperson_name ??
      item.salespersonName ??
      item.salesperson ??
      item.driver ??
      item.driver_name ??
      item.driverName ??
      '',
    type,
    cancelStatus: transactionType === 'CANCEL',
    canceledAt: item.canceled_at ?? item.canceledAt ?? null,
    status,
    transactionType,
    transactionStatus,
    payoutDate,
    pgProvider: item.pg_provider ?? item.pgProvider ?? '',
    contractId: item.contractId ?? item.contract_id ?? null,
    contractKey: item.contractKey,
    contract_key: item.contract_key,
    contractName: item.contractName ?? item.contract_name ?? null,
    contractDisplayLabel: transactionContractLabel(item),
    cardName,
    cardNo:
      item.card_no ??
      item.cardNo ??
      item.card_number ??
      item.cardNumber ??
      item.card_num ??
      item.cardNum ??
      '',
  };
}

/** 정산 응답의 payments를 해당 정산에 반영된 원장 행으로 변환한다. */
function mapSettlementPayments(rows) {
  return rows.flatMap((settlementRow, rowIndex) => {
    const payments = Array.isArray(settlementRow?.payments) ? settlementRow.payments : [];
    const settlementStatus = settlementRow?.status ?? settlementRow?.settlementStatus ?? '';
    const settlementStage = getSettlementStage(settlementStatus);
    const settlementStageLabel = getSettlementStageLabel(settlementStatus);
    const cancelPaymentIds = new Set(
      payments
        .filter((payment) => String(payment?.transaction_type ?? payment?.transactionType ?? '').toUpperCase() === 'CANCEL')
        .map((payment) => String(payment?.payment_transaction_id ?? payment?.paymentTransactionId ?? ''))
        .filter(Boolean)
    );
    const cancelRows = payments.filter(
      (payment) => String(payment?.transaction_type ?? payment?.transactionType ?? '').toUpperCase() === 'CANCEL'
    );
    let remainingFeeRefund = Math.max(0, getCanceledFeeRefundAmount(settlementRow));

    const mapped = payments.map((payment, paymentIndex) => {
      const tx = mapPaymentToTransaction(payment);
      const isCancel = tx.transactionType === 'CANCEL';
      const paymentId = String(tx.paymentTransactionId ?? '');
      const isCanceledApproval =
        !isCancel &&
        (String(tx.status || '').toUpperCase() === 'CANCELED' || tx.canceledAt != null);
      const canceledInThisSettlement = isCanceledApproval && cancelPaymentIds.has(paymentId);
      let amount = Math.abs(Number(tx.amount || 0));
      let fee = Number(tx.fee ?? 0);

      if (isCancel) {
        amount = -amount;
        const explicitRefund = Math.max(0, getCanceledFeeRefundAmount(payment));
        const matchingApproval = payments.find((candidate) => {
          const candidateType = String(candidate?.transaction_type ?? candidate?.transactionType ?? '').toUpperCase();
          const candidateId = String(candidate?.payment_transaction_id ?? candidate?.paymentTransactionId ?? '');
          return candidateType === 'APPROVAL' && candidateId === paymentId;
        });
        const matchingApprovalFee = Number(
          matchingApproval?.cp_fee_amount ??
          matchingApproval?.cpFeeAmount ??
          matchingApproval?.fee_amount ??
          matchingApproval?.feeAmount ??
          0
        );
        const cancelIndex = cancelRows.indexOf(payment);
        const isLastCancel = cancelIndex === cancelRows.length - 1;
        let refund = explicitRefund;
        if (refund <= 0 && remainingFeeRefund > 0) {
          refund = isLastCancel
            ? remainingFeeRefund
            : Math.min(Math.max(0, matchingApprovalFee), remainingFeeRefund);
        }
        remainingFeeRefund = Math.max(0, remainingFeeRefund - refund);
        fee = -refund;
      }

      const lineStatus = isCancel
        ? '취소 반영'
        : canceledInThisSettlement
          ? '취소 원승인'
          : isCanceledApproval
            ? '후일 취소'
            : '승인 반영';

      return {
        ...tx,
        id: tx.id ?? tx.paymentTransactionId ?? `settlement-${rowIndex}-${paymentIndex}`,
        amount,
        fee,
        settlement: amount - fee,
        settlementStatus,
        settlementStage,
        settlementStageLabel,
        settlementLineStatus: lineStatus,
      };
    });

    const targetAmount =
      Number(settlementRow?.gross_amount ?? settlementRow?.grossAmount ?? 0) -
      Number(settlementRow?.cancel_amount ?? settlementRow?.cancelAmount ?? 0);
    const targetFee = getSettlementFeeAmount(settlementRow);
    const targetSettlement = Number(
      settlementRow?.net_amount ??
      settlementRow?.netAmount ??
      (targetAmount - targetFee)
    );
    const mappedAmount = mapped.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    const mappedFee = mapped.reduce((sum, item) => sum + Number(item.fee || 0), 0);
    const mappedSettlement = mapped.reduce((sum, item) => sum + Number(item.settlement || 0), 0);
    const amountAdjustment = targetAmount - mappedAmount;
    const feeAdjustment = targetFee - mappedFee;
    const settlementAdjustment = targetSettlement - mappedSettlement;

    if (amountAdjustment !== 0 || feeAdjustment !== 0 || settlementAdjustment !== 0) {
      mapped.push({
        id: `settlement-adjustment-${settlementRow?.id ?? rowIndex}`,
        paymentTransactionId: '',
        date: settlementRow?.settlement_date ?? settlementRow?.settlementDate ?? '',
        pharmacy: '정산 조정',
        pgProvider: settlementRow?.pg_provider ?? settlementRow?.pgProvider ?? '',
        amount: amountAdjustment,
        fee: feeAdjustment,
        settlement: settlementAdjustment,
        transactionType: amountAdjustment < 0 ? 'CANCEL' : 'ADJUSTMENT',
        status: '',
        settlementStatus,
        settlementStage,
        settlementStageLabel,
        settlementLineStatus: '이월·조정',
      });
    }

    return mapped;
  });
}

function mapSettlementToTransaction(item, index) {
  const paymentTransactionId =
    item.payment_transaction_id ??
    item.paymentTransactionId ??
    item.id ??
    '';
  const id = paymentTransactionId || `settle-${index}`;

  // 결제일: created_at 사용 (년.월.일)
  const dateStr =
    item.created_at ??
    item.createdAt ??
    item.settlement_date ??
    item.settlementDate ??
    item.transfer_date ??
    item.transferDate ??
    item.date ??
    '';
  const date = dateStr ? dayjs.utc(dateStr).toISOString() : dayjs().toISOString();
  const payoutDate =
    item.transfer_date ??
    item.transferDate ??
    item.deposit_date ??
    item.depositDate ??
    item.payout_date ??
    item.payoutDate ??
    item.settlement_date ??
    item.settlementDate ??
    '';

  const grossAmount = Number(
    item.gross_amount ??
    item.grossAmount ??
    item.amount ??
    item.pg_trd_amt ??
    item.request_trd_amt ??
    item.trd_amt ??
    item.trdAmt ??
    0
  );
  const cancelAmount = Number(
    item.cancel_amount ??
    item.cancelAmount ??
    0
  );
  // 결제금액 = gross_amount - cancel_amount
  const amount = grossAmount - cancelAmount;
  // 수수료 = fee_amount - canceled_fee_refund_amount
  const fee = getSettlementFeeAmount(item);
  // 정산금액 = net_amount
  const settlement = Number(
    item.net_amount ??
    item.netAmount ??
    (amount - fee)
  );

  return {
    id,
    paymentTransactionId,
    receiptTransactionId: item.payment_transaction_id ?? item.paymentTransactionId,
    paymentId: paymentTransactionId,
    date,
    pharmacy:
      item.cp_name ??
      item.cpName ??
      item.pharmacy ??
      item.pharmacy_name ??
      item.pharmacyName ??
      item.mcht_cust_nm ??
      item.mchtCustNm ??
      item.customer_name ??
      item.customerName ??
      '-',
    amount,
    fee,
    settlement,
    approvalNo: item.approval_no ?? item.approvalNumber ?? item.appr_no ?? item.apprNo ?? '',
    type: '-',
    cancelStatus: false,
    status: item.status ?? '',
    payoutDate,
    pgProvider: item.pg_provider ?? item.pgProvider ?? '',
    cardName: '-',
    cardNo: '',
  };
}

function getStatusLabelFromTx(tx) {
  if (isUnknownPayment(tx)) return unknownPaymentLabel(tx);
  const status = String(tx.transactionStatus || '').toUpperCase();
  return TRANSACTION_STATUS_LABELS[status] || tx.transactionStatus || '-';
}

function getDepositAmount(row) {
  return Number(
    row?.depositAmount ??
    row?.deposit_amount ??
    row?.paidAmount ??
    row?.paid_amount ??
    row?.transferAmount ??
    row?.transfer_amount ??
    row?.payoutAmount ??
    row?.payout_amount ??
    0
  );
}

function getSettlementNetAmount(row) {
  return Number(
    row?.settlementNetAmount ??
    row?.settlement_net_amount ??
    row?.settlementAmount ??
    row?.settlement_amount ??
    0
  );
}

/** 결제내역 CSV: 결제일(시:분), 승인번호, 약국, 카드사, 할부, 금액, 수수료, 정산, 영업자, 상태 */
function buildPaymentCsv(data, options = {}) {
  const { includePayoutDate = false } = options;
  const columns = [
    { key: 'date', label: '결제시간' },
    { key: 'payoutDate', label: '지급일' },
    { key: 'approvalNo', label: '승인번호' },
    { key: 'pharmacy', label: '약국' },
    { key: 'cardName', label: '카드사' },
    { key: 'type', label: '할부' },
    { key: 'amount', label: '금액' },
    { key: 'fee', label: '수수료' },
    { key: 'settlement', label: '정산' },
    { key: 'driver', label: '영업자' },
    { key: 'status', label: '상태' },
  ];
  const finalColumns = includePayoutDate
    ? columns
    : columns.filter((c) => c.key !== 'payoutDate');
  const escapeCell = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).replace(/"/g, '""');
  };
  const header = finalColumns.map((c) => `"${c.label}"`).join(',');
  const lines = data.map((row) =>
    finalColumns
      .map((c) => {
        let v;
        if (c.key === 'date') {
          v = formatKST(row.date, 'YYYY.MM.DD HH:mm');
        } else if (c.key === 'amount') {
          v = Number(row.amount || 0);
        } else if (c.key === 'fee') {
          v = Number(row.fee ?? 0);
        } else if (c.key === 'settlement') {
          const settlement = Number(row.settlement);
          v = Number.isFinite(settlement)
            ? settlement
            : Number(row.amount || 0) - Number(row.fee ?? 0);
        } else if (c.key === 'status') {
          v = getStatusLabelFromTx(row);
        } else if (c.key === 'payoutDate') {
          v = row?.payoutDate ? formatKST(row.payoutDate, 'YYYY.MM.DD') : '-';
        } else {
          v = row[c.key];
        }
        return `"${escapeCell(v ?? '')}"`;
      })
      .join(','),
  );
  return '\uFEFF' + header + '\r\n' + lines.join('\r\n');
}

/** 정산내역 CSV: 결제일(월.일), 약국, 금액, 수수료, 정산 */
function buildSettlementCsv(data, mode, pgNameResolver) {
  const columns =
    mode === 'deposit'
      ? [
          { key: 'date', label: '결제시간' },
          { key: 'pgName', label: 'PG사' },
          { key: 'amount', label: '금액' },
          { key: 'fee', label: '수수료' },
          { key: 'settlement', label: '정산' },
        ]
      : [
          { key: 'date', label: '결제시간' },
          { key: 'pharmacy', label: '약국' },
          { key: 'pgName', label: 'PG사' },
          { key: 'amount', label: '금액' },
          { key: 'fee', label: '수수료' },
          { key: 'settlement', label: '정산' },
          { key: 'settlementStatus', label: '상태' },
        ];
  const escapeCell = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).replace(/"/g, '""');
  };
  const header = columns.map((c) => `"${c.label}"`).join(',');
  const lines = data.map((row) =>
    columns
      .map((c) => {
        let v;
        if (c.key === 'date') {
          v = formatKST(row.date, 'YYYY.MM.DD HH:mm');
        } else if (c.key === 'amount') {
          v = Number(row.amount || 0);
        } else if (c.key === 'fee') {
          v = Number(row.fee ?? 0);
        } else if (c.key === 'settlement') {
          const settlement = Number(row.settlement);
          v = Number.isFinite(settlement)
            ? settlement
            : Number(row.amount || 0) - Number(row.fee ?? 0);
        } else if (c.key === 'pgName') {
          v = pgNameResolver ? pgNameResolver(row.pgProvider) : (row.pgProvider || '-');
        } else if (c.key === 'settlementStatus') {
          const lineStatus = row.settlementLineStatus || '-';
          const stageLabel = row.settlementStageLabel || '';
          v = stageLabel ? `${lineStatus} (${stageLabel})` : lineStatus;
        } else {
          v = row[c.key];
        }
        return `"${escapeCell(v ?? '')}"`;
      })
      .join(','),
  );
  return '\uFEFF' + header + '\r\n' + lines.join('\r\n');
}

/** 수기결제 팝업 옵션 (MainLayout과 동일) */
function openSugiPopup() {
  const href = `${window.location.origin}/cp/sugi`;
  const w = 450;
  const h = 750;
  // 현재 브라우저 창 위치/크기 기준으로 정중앙 계산
  const dualScreenLeft = window.screenLeft ?? window.screenX ?? 0;
  const dualScreenTop = window.screenTop ?? window.screenY ?? 0;
  const winWidth =
    window.innerWidth ??
    document.documentElement.clientWidth ??
    window.screen?.width ??
    800;
  const winHeight =
    window.innerHeight ??
    document.documentElement.clientHeight ??
    window.screen?.height ??
    600;
  const left = dualScreenLeft + Math.max(0, (winWidth - w) / 2);
  const top = dualScreenTop + Math.max(0, (winHeight - h) / 2);
  const popupOptions = `width=${w},height=${h},scrollbars=yes,resizable=yes,left=${Math.round(
    left,
  )},top=${Math.round(top)}`;
  window.open(href, 'sugiPopup', popupOptions);
}


function IntegratedSettlement() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const recoveryView = isUnmePaymentRecovery(location.search);
  const handleContractAuthError = useCallback(() => {
    dispatch(setOtpVerified(false));
    dispatch(checkLogin(false));
    navigate('/cp/login', { replace: true });
  }, [dispatch, navigate]);
  const contractAvailability = useContractBreakdownAvailability(handleContractAuthError);
  const [showMonthlyContracts, setShowMonthlyContracts] = useState(false);
  const [showDailyContracts, setShowDailyContracts] = useState(false);
  const [resultStatusFilter, setResultStatusFilter] = useState('');
  const { showToast } = useToast();
  const { showAllOpen, toggleShowAll } = useTooltip();
  const cpSupplierInfo = useSelector((state) => state.cpSupplierInfo?.info);
  const cpPgMerchantRows = useSelector((state) => state.cpPgMerchantInfo?.rows) || [];
  const [hoveredTooltipId, setHoveredTooltipId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    if (!showAllOpen) setHoveredTooltipId(null);
  }, [showAllOpen]);

  const [activeStartDate, setActiveStartDate] = useState(new Date());
  const [calendarRows, setCalendarRows] = useState([]);
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);

  const [transactions, setTransactions] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState(null);
  /** 정산 API의 data.rows (각 row.payments를 내역으로 표시, 합산은 row 기준) */
  const [settlementRows, setSettlementRows] = useState([]);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [receiptEmail, setReceiptEmail] = useState('');
  const [isSendingReceipt, setIsSendingReceipt] = useState(false);
  const [sendMethod, setSendMethod] = useState('email'); // 'email' | 'sms' | 'kakao'
  const [receiptPhone, setReceiptPhone] = useState('');
  const [receiptPreviewData, setReceiptPreviewData] = useState(null);
  const receiptEmailLookup = useCurrentReceiptEmail({
    role: 'cp', open: receiptModalOpen, transactionId: receiptPreviewData?.customerEmailTransactionId,
    setEmail: setReceiptEmail,
    enabled: ['1', 'true', 'yes', 'y'].includes(String(process.env.REACT_APP_RECEIPT_CUSTOMER_EMAIL_PREFILL_ENABLED || '').trim().toLowerCase()),
  });
  const receiptInfoRequestRef = useRef(0);
  useEffect(() => () => { receiptInfoRequestRef.current += 1; }, []);
  const [receiptViewMode, setReceiptViewMode] = useState('approval'); // 'approval' | 'cancel' - 전표 모달 내 토글, API 연동 시 상태값 호출용
  const [cpInfo, setCpInfo] = useState(null);
  const [revealModalOpen, setRevealModalOpen] = useState(false);
  const [revealPassword, setRevealPassword] = useState('');
  const [revealReason, setRevealReason] = useState('');
  const [revealLoading, setRevealLoading] = useState(false);
  const receiptSupplierInfo = {
    supplierName: cpSupplierInfo?.name || RECEIPT_SUPPLIER_INFO.supplierName,
    supplierOwnerName: cpSupplierInfo?.owner_name || RECEIPT_SUPPLIER_INFO.supplierOwnerName,
    supplierBusinessNo: cpSupplierInfo?.business_no || RECEIPT_SUPPLIER_INFO.supplierBusinessNo,
    supplierContact: cpSupplierInfo?.contact || RECEIPT_SUPPLIER_INFO.supplierContact,
    supplierAddress: cpSupplierInfo?.address || RECEIPT_SUPPLIER_INFO.supplierAddress,
  };
  const getPgName = useCallback((pgProvider) => {
    const provider = String(pgProvider || '').toUpperCase();
    const matched = cpPgMerchantRows.find(
      (row) => String(row?.pg_provider || '').toUpperCase() === provider,
    );
    return matched?.name || pgProvider || '-';
  }, [cpPgMerchantRows]);

  const getReceiptCardMerchantInfo = useCallback((pgProvider) => {
    const provider = String(pgProvider || '').toUpperCase();
    const matched = cpPgMerchantRows.find(
      (row) => String(row?.pg_provider || '').toUpperCase() === provider,
    );
    if (!matched) return RECEIPT_CARD_MERCHANT_INFO;
    return {
      cardMerchantName: matched.name || RECEIPT_CARD_MERCHANT_INFO.cardMerchantName,
      cardMerchantOwnerName: matched.owner_name || RECEIPT_CARD_MERCHANT_INFO.cardMerchantOwnerName,
      cardMerchantBusinessNo: matched.business_no || RECEIPT_CARD_MERCHANT_INFO.cardMerchantBusinessNo,
      cardMerchantContact: matched.contact || matched.phone || RECEIPT_CARD_MERCHANT_INFO.cardMerchantContact,
      cardMerchantAddress: matched.address || RECEIPT_CARD_MERCHANT_INFO.cardMerchantAddress,
    };
  }, [cpPgMerchantRows]);

  // 페이지 진입 시 1회 가맹점 정보 조회 (전표·영수증 가맹점 영역에 사용)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cp = await cpMe();
        if (!cancelled) setCpInfo(cp || null);
      } catch (e) {
        // 가맹점 정보가 없어도 페이지는 동작해야 하므로 토스트만 선택적으로 표시
        // showToast(e.message || '가맹점 정보를 불러오지 못했습니다.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // 영수증 미리보기 모달 열기 (승인/취소는 모달 내 토글로 전환, 나중에 API에서 상태값 호출 예정)
  const openReceiptPreview = useCallback(async (item) => {
    if (!item) return;
    const amount = Number(item.amount || 0);
    const fee = Number(item.fee ?? 0);

    // 기본 카드번호 마스킹은 완전 마스킹 (****-****-****-****)
    const cardNumberMasked = '****-****-****-****';

    // 금액 계산 (과세금액, 부가세)
    const taxableAmount = Math.round(amount / 1.1);
    const vatAmount = amount - taxableAmount;
    const taxFreeAmount = 0;
    const serviceAmount = 0;

    const approvalDateTime = formatKST(item.date, 'YYYY.MM.DD HH:mm:ss');
    const cancelDateTime = item.canceledAt
      ? formatKST(item.canceledAt, 'YYYY.MM.DD HH:mm:ss')
      : null;
    const isCanceled = !!item.cancelStatus;
    const receiptCardMerchantInfo = getReceiptCardMerchantInfo(item.pgProvider);

    const requestId = ++receiptInfoRequestRef.current;
    const previewData = {
      customerEmailTransactionId: item.customerEmailTransactionId,
      paymentTransactionId: item.paymentTransactionId || '',
      cardBrand: item.cardName || '-',
      cardNumberMasked: cardNumberMasked,
      installmentMonth: item.type || '일시불',
      approvalNumber: item.approvalNo || '-',
      approvalDateTime,
      cancelDateTime,
      isCanceled,
      pharmacyName: item.pharmacy || '-',
      taxableAmount: taxableAmount.toLocaleString('ko-KR') + '원',
      vatAmount: vatAmount.toLocaleString('ko-KR') + '원',
      taxFreeAmount: taxFreeAmount.toLocaleString('ko-KR') + '원',
      serviceAmount: serviceAmount.toLocaleString('ko-KR') + '원',
      totalAmount: amount.toLocaleString('ko-KR') + '원',
      ...receiptSupplierInfo,
      merchantName: cpInfo?.name || '-',
      merchantOwnerName: cpInfo?.boss_name || '-',
      merchantBusinessNo: cpInfo?.business_no || '-',
      merchantContact: cpInfo?.phone || '-',
      merchantAddress: cpInfo?.address || '-',
      ...receiptCardMerchantInfo,
    };
    let resolved;
    try {
      resolved = await resolveUnmeReceiptInfo({ role: 'cp', transactionId: item.receiptTransactionId, previewData });
    } catch (error) {
      if (requestId === receiptInfoRequestRef.current) showToast(error.message, 'error');
      return;
    }
    if (requestId !== receiptInfoRequestRef.current) return;
    setReceiptPreviewData(resolved);
    setReceiptViewMode('approval');
    setReceiptModalOpen(true);
    setReceiptEmail('');
  }, [cpInfo, getReceiptCardMerchantInfo, receiptSupplierInfo, showToast]);

  // 카드번호 조회 모달에서 확인 버튼 클릭 시
  const handleRevealCardNumber = useCallback(async () => {
    if (!receiptPreviewData?.paymentTransactionId) {
      showToast('전표 ID가 없습니다.');
      return;
    }
    const password = (revealPassword || '').trim();
    if (!password) {
      showToast('비밀번호를 입력해 주세요.');
      return;
    }
    const reason = (revealReason || '').trim();
    if (!reason) {
      showToast('조회 사유를 입력해 주세요.');
      return;
    }
    setRevealLoading(true);
    try {
      const { data } = await axios.post(
        baseUrl + '/api/cp/payment/manual/reveal-card',
        {
          paymentTransactionId: receiptPreviewData.paymentTransactionId,
          password,
          reason,
        },
        { withCredentials: true }
      );
      if (data?.code !== 1000) {
        throw new Error(data?.msg || '카드번호 조회에 실패했습니다.');
      }
      const masked = data?.data?.card_no_masked;
      if (!masked) {
        throw new Error('카드번호를 가져오지 못했습니다.');
      }
      setReceiptPreviewData((prev) =>
        prev ? { ...prev, cardNumberMasked: masked } : prev
      );
      setRevealModalOpen(false);
      setRevealPassword('');
      setRevealReason('');
      showToast('카드번호가 조회되었습니다.');
    } catch (e) {
      showToast(e.message || '카드번호 조회에 실패했습니다.', 'error');
    } finally {
      setRevealLoading(false);
    }
  }, [receiptPreviewData, revealPassword, revealReason, showToast]);
  const [excelDownloading, setExcelDownloading] = useState(false);
  const [dailyExcelDownloading, setDailyExcelDownloading] = useState(false);
  const [viewMode, setViewMode] = useState(() => recoveryView && new URLSearchParams(location.search).get('view') === 'payment' ? 'payment' : 'deposit'); // 'payment' | 'settlement' | 'deposit'
  const [paymentSearchKeyword, setPaymentSearchKeyword] = useState('');

  const activeMonthKey = useMemo(
    () => dayjs(activeStartDate).format('YYYY-MM'),
    [activeStartDate]
  );

  useEffect(() => {
    const fetchCalendarSummary = async () => {
      try {
        const monthKeys = [
          dayjs(activeStartDate).subtract(1, 'month').format('YYYY-MM'),
          activeMonthKey,
          dayjs(activeStartDate).add(1, 'month').format('YYYY-MM'),
        ];

        const responses = await Promise.all(
          monthKeys.map((month) =>
            axios.get(baseUrl + '/api/cp/summary/calendar', {
              params: { month },
              withCredentials: true,
            })
          )
        );

        const mergedByDate = new Map();
        responses.forEach(({ data }) => {
          const rows = data?.code === 1000 && Array.isArray(data?.data?.rows)
            ? data.data.rows
            : [];
          rows.forEach((row) => {
            if (!row?.date) return;
            mergedByDate.set(row.date, row);
          });
        });

        setCalendarRows(Array.from(mergedByDate.values()));
      } catch {
        setCalendarRows([]);
      }
    };
    fetchCalendarSummary();
  }, [activeMonthKey, activeStartDate, calendarRefreshKey]);

  const summaryMap = useMemo(() => {
    const map = {};
    calendarRows.forEach((row) => {
      if (!row?.date) return;
      map[row.date] = row;
    });
    return map;
  }, [calendarRows]);

  /** 해당월(달력 기준) 매출·정산·입금 총액 */
  const monthlyTotals = useMemo(() => {
    const y = dayjs(activeStartDate).year();
    const m = dayjs(activeStartDate).month();
    let revenue = 0;
    let settlement = 0;
    let deposit = 0;
    calendarRows.forEach((row) => {
      if (!row?.date) return;
      const d = dayjs(row.date);
      if (d.year() === y && d.month() === m) {
        const pay = Number(row.paymentAmount || 0);
        const cancel = Number(row.cancelAmount || 0);
        revenue += pay - cancel;
        settlement += getSettlementNetAmount(row);
        deposit += getDepositAmount(row);
      }
    });
    return { revenue, settlement, deposit };
  }, [activeStartDate, calendarRows]);

  const selectedDateKey = dayjs(selectedDate).format('YYYY-MM-DD');

  /** 달력에서 보고 있는 월 1일~말일 기준 월간 엑셀 다운로드 */
  const handleExcelDownload = useCallback(async () => {
    const monthStart = dayjs(activeStartDate).startOf('month');
    const monthEnd = dayjs(activeStartDate).endOf('month');
    const startStr = monthStart.format('YYYYMMDD');
    const endStr = monthEnd.format('YYYYMMDD');
    setExcelDownloading(true);
    try {
      if (PHPAY_INTEGRATED_SETTLEMENT_EXPORT_ENABLED) {
        await downloadBlob({
          url: '/api/cp/integrated-settlement/export',
          params: {
            dateFrom: monthStart.format('YYYY-MM-DD'),
            dateTo: monthEnd.format('YYYY-MM-DD'),
            type: 'ALL',
          },
          fallbackFilename: `통합정산_${startStr}_${endStr}.xlsx`,
        });
        showToast('통합정산 엑셀 다운로드가 시작되었습니다.');
        return;
      }

      const rows = await fetchManualPaymentExportRows(baseUrl, {
        dateFrom: monthStart.format('YYYY-MM-DD'),
        dateTo: monthEnd.format('YYYY-MM-DD'),
      });
      const list = (rows || []).map(mapPaymentToTransaction);
      if (list.length === 0) {
        showToast('해당 기간 월간 매출, 정산, 입금내역이 없습니다.');
        return;
      }
      const csv = buildPaymentCsv(list, { includePayoutDate: true });
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const filename = `월간 매출, 정산, 입금내역_${startStr}_${endStr}.csv`;
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showToast('엑셀 다운로드가 시작되었습니다.');
    } catch (error) {
      showToast(error?.message || '다운로드 중 오류가 발생했습니다.', 'error');
    } finally {
      setExcelDownloading(false);
    }
  }, [activeStartDate, showToast]);

  const fetchPaymentsForDate = useCallback(async (dateKey, searchKeyword = '', quiet = false) => {
    if (!dateKey) {
      setTransactions([]);
      setPaymentSummary(null);
      return;
    }
    if (!quiet) { setPaymentSummary(null); setPaymentLoading(true); }
    try {
      const kw = (searchKeyword || '').trim();
      let page = 1;
      let hasNext = false;
      let rows = [];
      let summary = null;

      do {
        const params = withUnmePaymentRecoveryParams({ page, limit: 100, dateFrom: dateKey, dateTo: dateKey }, location.search);
        if (resultStatusFilter) params.status = resultStatusFilter;
        if (kw) params.pharmacyName = kw;
        const { data } = await axios.get(baseUrl + '/api/cp/payment/manual', {
          params,
          timeout: 10000,
          withCredentials: true,
        });
        if (data?.code !== 1000) throw new Error('결과 조회 실패');

        const responseData = data.data || {};
        const pageRows = Array.isArray(responseData.rows)
          ? responseData.rows
          : Array.isArray(responseData)
            ? responseData
            : [];
        rows = rows.concat(pageRows);
        if (!summary && responseData.summary) summary = responseData.summary;
        const limit = Number(responseData.limit ?? params.limit);
        const total = Number(responseData.total ?? rows.length);
        hasNext = responseData.hasNext !== undefined
          ? Boolean(responseData.hasNext)
          : page * limit < total;
        page += 1;
      } while (hasNext);

      const mappedRows = rows.map(mapPaymentToTransaction);
      const approvalFeeByPaymentId = new Map(
        mappedRows
          .filter((item) => item.transactionType === 'APPROVAL')
          .map((item) => [String(item.paymentTransactionId ?? ''), Math.abs(Number(item.fee || 0))])
      );
      const normalizedRows = mappedRows.map((item) => {
        if (item.transactionType !== 'CANCEL') return item;
        const approvalFee = approvalFeeByPaymentId.get(String(item.paymentTransactionId ?? ''));
        return {
          ...item,
          fee: -Math.abs(approvalFee ?? Number(item.fee || 0)),
        };
      });
      setTransactions(normalizedRows);
      setPaymentSummary(summary ? {
        transactionCount: Number(summary.transactionCount ?? summary.transaction_count ?? normalizedRows.length),
        approvalCount: Number(summary.approvalCount ?? summary.approval_count ?? 0),
        cancelCount: Number(summary.cancelCount ?? summary.cancel_count ?? 0),
        feeAmount: summary.feeAmount ?? summary.fee_amount ?? null,
        failedCount: Number(summary.failedCount ?? summary.failed_count ?? 0),
        approvalAmount: Number(summary.approvalAmount ?? summary.approval_amount ?? 0),
        cancelAmount: Number(summary.cancelAmount ?? summary.cancel_amount ?? 0),
        netPaymentAmount: Number(summary.netPaymentAmount ?? summary.net_payment_amount ?? 0),
      } : null);
    } catch {
      if (quiet) return;
      setTransactions([]);
      setPaymentSummary(null);
    } finally {
      setPaymentLoading(false);
    }
  }, [resultStatusFilter, location.search]);

  const fetchSettlementsForDate = useCallback(async (dateKey) => {
    if (!dateKey) {
      setSettlementRows([]);
      setTransactions([]);
      return;
    }
    setPaymentLoading(true);
    const dateFrom = dayjs.utc(dateKey + 'T00:00:00').subtract(9, 'hour').toISOString();
    const dateTo = dayjs.utc(dateKey + 'T23:59:59.999').subtract(9, 'hour').toISOString();
    try {
      const { data } = await axios.get(baseUrl + '/api/cp/settlement', {
        params: { page: 1, limit: 500, dateFrom, dateTo },
        withCredentials: true,
      });
      const rows = Array.isArray(data?.data?.rows) ? data.data.rows : [];
      setSettlementRows(rows);
      // 현재 결제 상태와 무관하게 해당 정산에 반영된 승인·취소 원장 행을 모두 표시한다.
      setTransactions(mapSettlementPayments(rows));
    } catch {
      setSettlementRows([]);
      setTransactions([]);
    } finally {
      setPaymentLoading(false);
    }
  }, []);

  const fetchDepositsForDate = useCallback(async (dateKey) => {
    if (!dateKey) {
      setSettlementRows([]);
      setTransactions([]);
      return;
    }
    setPaymentLoading(true);
    const dateFrom = dayjs.utc(dateKey + 'T00:00:00').subtract(9, 'hour').toISOString();
    const dateTo = dayjs.utc(dateKey + 'T23:59:59.999').subtract(9, 'hour').toISOString();
    try {
      const { data } = await axios.get(baseUrl + '/api/cp/settlement/deposits', {
        params: { page: 1, limit: 500, dateFrom, dateTo },
        withCredentials: true,
      });
      const rows = Array.isArray(data?.data?.rows) ? data.data.rows : [];
      setSettlementRows(rows);
      // 입금내역은 이체일(입금일) 기준 정산 row를 그대로 표시
      const list = rows.map((r, i) => mapSettlementToTransaction(r, i));
      setTransactions(list);
    } catch {
      setSettlementRows([]);
      setTransactions([]);
    } finally {
      setPaymentLoading(false);
    }
  }, []);

  useEffect(() => {
    if (viewMode === 'settlement') {
      fetchSettlementsForDate(selectedDateKey);
    } else if (viewMode === 'deposit') {
      fetchDepositsForDate(selectedDateKey);
    } else {
      fetchPaymentsForDate(selectedDateKey, '');
    }
  }, [selectedDateKey, viewMode, fetchPaymentsForDate, fetchSettlementsForDate, fetchDepositsForDate]);

  // viewMode에 따라 필터링된 거래 목록 (결제내역은 API에서 검색하므로 클라이언트 필터링 제거)
  const displayedTransactions = useMemo(() => {
    return transactions;
  }, [transactions]);

  /** 선택된 날짜의 결제내역/정산내역 엑셀 다운로드 */
  const handleDailyExcelDownload = useCallback(async () => {
    if (!selectedDateKey || displayedTransactions.length === 0) {
      showToast('다운로드할 데이터가 없습니다.');
      return;
    }
    setDailyExcelDownloading(true);
    try {
      if (PHPAY_INTEGRATED_SETTLEMENT_EXPORT_ENABLED) {
        const exportType = viewMode === 'settlement'
          ? 'SETTLEMENT'
          : viewMode === 'deposit'
            ? 'DEPOSIT'
            : 'PAYMENT';
        const q = paymentSearchKeyword.trim();
        const dateStr = dayjs(selectedDate).format('YYYYMMDD');
        await downloadBlob({
          url: '/api/cp/integrated-settlement/export',
          params: {
            dateFrom: selectedDateKey,
            dateTo: selectedDateKey,
            type: exportType,
            ...(viewMode === 'payment' && q ? { q } : {}),
          },
          fallbackFilename: `통합정산_${dateStr}_${dateStr}.xlsx`,
        });
        showToast('통합정산 엑셀 다운로드가 시작되었습니다.');
        return;
      }

      const csv = (viewMode === 'settlement' || viewMode === 'deposit')
        ? buildSettlementCsv(displayedTransactions, viewMode, getPgName)
        : buildPaymentCsv(displayedTransactions);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const dateStr = dayjs(selectedDate).format('YYYYMMDD');
      const filename = viewMode === 'settlement'
        ? `정산내역_${dateStr}.csv`
        : viewMode === 'deposit'
          ? `입금내역_${dateStr}.csv`
          : `결제내역_${dateStr}.csv`;
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showToast('엑셀 다운로드가 시작되었습니다.');
    } catch (error) {
      showToast(error?.message || '다운로드 중 오류가 발생했습니다.', 'error');
    } finally {
      setDailyExcelDownloading(false);
    }
  }, [selectedDate, selectedDateKey, displayedTransactions, viewMode, paymentSearchKeyword, showToast, getPgName]);

  const daySummary = summaryMap[selectedDateKey];
  const paymentAmount = Number(daySummary?.paymentAmount ?? daySummary?.payment_amount ?? 0);
  const paymentCount = Number(daySummary?.paymentCount ?? daySummary?.payment_count ?? 0);
  const cancelAmount = Number(daySummary?.cancelAmount ?? daySummary?.cancel_amount ?? 0);
  const cancelCount = Number(daySummary?.cancelCount ?? daySummary?.cancel_count ?? 0);
  const settlementAmount = getSettlementNetAmount(daySummary);
  const settlementCount = Number(daySummary?.settlementCount ?? daySummary?.settlement_count ?? 0);
  const depositAmount = getDepositAmount(daySummary);
  const revenueAmount = paymentSummary
    ? Number(paymentSummary.netPaymentAmount || 0)
    : paymentAmount - cancelAmount;
  const summaryFeeSource =
    daySummary?.settlementFeeAmount ??
    daySummary?.settlement_fee_amount ??
    daySummary?.feeAmount ??
    daySummary?.fee_amount ??
    daySummary?.cpFeeAmount ??
    daySummary?.cp_fee_amount;
  const listedPaymentFeeAmount = transactions.reduce((sum, item) => {
    const status = String(item.transactionStatus || '').toUpperCase();
    if (isUnknownPayment(item) || status === 'FAILED') return sum;
    return sum + Number(item.fee || 0);
  }, 0);
  const summaryFeeAmount = paymentSummary
    ? (paymentSummary.feeAmount != null ? Number(paymentSummary.feeAmount) : listedPaymentFeeAmount)
    : summaryFeeSource == null
      ? revenueAmount - settlementAmount
      : Number(summaryFeeSource) - getCanceledFeeRefundAmount(daySummary);
  const paymentSettlementAmount = paymentSummary
    ? revenueAmount - summaryFeeAmount
    : settlementAmount;
  const totalCount = viewMode === 'payment'
    ? Number(paymentSummary?.transactionCount ?? paymentCount + cancelCount)
    : displayedTransactions.length;
  const hasSales = paymentAmount > 0 || cancelAmount > 0 || revenueAmount !== 0;
  const hasSettlement = settlementAmount > 0;
  const hasDeposit = depositAmount > 0;
  const settlementStages = useMemo(
    () => new Set(settlementRows.map((row) => getSettlementStage(row?.status ?? row?.settlementStatus))),
    [settlementRows]
  );
  const isPendingSettlement =
    viewMode === 'settlement' && settlementStages.size === 1 && settlementStages.has('pending');

  /** 결제내역은 캘린더 요약, 정산·입금내역은 정산 API 기준으로 합산 */
  const { totalAmount, totalFee, totalSettlement } = useMemo(() => {
    if (viewMode === 'payment') {
      return {
        totalAmount: revenueAmount,
        totalFee: summaryFeeAmount,
        totalSettlement: paymentSettlementAmount,
      };
    }
    if (viewMode === 'deposit' && settlementRows.length > 0) {
      let amount = 0;
      let fee = 0;
      let settle = 0;
      settlementRows.forEach((r) => {
        const gross = Number(r.gross_amount ?? r.grossAmount ?? 0);
        const cancel = Number(r.cancel_amount ?? r.cancelAmount ?? 0);
        amount += gross - cancel;
        fee += getSettlementFeeAmount(r);
        settle += Number(r.net_amount ?? r.netAmount ?? 0);
      });
      return { totalAmount: amount, totalFee: fee, totalSettlement: settle };
    }
    let amount = 0;
    let fee = 0;
    let settle = 0;
    displayedTransactions.forEach((item) => {
      amount += Number(item.amount || 0);
      fee += Number(item.fee ?? 0);
      const s = Number(item.settlement);
      settle += Number.isFinite(s) ? s : Number(item.amount || 0) - Number(item.fee ?? 0);
    });
    return { totalAmount: amount, totalFee: fee, totalSettlement: settle };
  }, [
    viewMode,
    settlementRows,
    displayedTransactions,
    revenueAmount,
    summaryFeeAmount,
    paymentSettlementAmount,
  ]);

  const handleConfirmCancel = async () => {
    if (!selectedItem) return;
    const paymentTransactionId = selectedItem.paymentTransactionId;
    if (paymentTransactionId == null) {
      showToast('취소할 결제 ID가 없습니다.');
      return;
    }
    setCancelModalOpen(false);
    try {
      const { data } = await axios.post(
        baseUrl + '/api/cp/payment/manual/cancel',
        { paymentTransactionId, reason: '가맹점 취소 요청' },
        { withCredentials: true }
      );
      if (showUnknownPayment(data, 'cp', true)) return;
      const status = data?.data?.status;
      const providerResult = data?.data?.provider_result || {};
      const success =
        providerResult?.success !== false && status === 'COMPLETED';
      if (data?.code === 1000 && success) {
        showToast(data?.msg || '결제취소가 완료되었습니다.');
        fetchPaymentsForDate(selectedDateKey);
        setCalendarRefreshKey((key) => key + 1);
        setSelectedItem(null);
      } else {
        showToast(
          data?.data?.outRsltMsg ?? data?.data?.provider_result?.outRsltMsg ?? data?.msg ?? '결제취소에 실패했습니다.',
          'error'
        );
      }
    } catch (err) {
      if (showUnknownPayment(err?.response?.data, 'cp', true)) return;
      showToast(
        err?.response?.data?.msg || '결제취소에 실패했습니다.',
        'error'
      );
    }
  };

  const handleConfirmSendReceipt = async () => {
    if (!receiptPreviewData?.paymentTransactionId) {
      showToast('전표 ID가 없습니다.', 'error');
      return;
    }

    const closeReceipt = () => {
      receiptInfoRequestRef.current += 1;
      setReceiptModalOpen(false);
      setReceiptEmail('');
      setReceiptPhone('');
      setSendMethod('email');
      setReceiptPreviewData(null);
      setReceiptViewMode('approval');
    };

    if (sendMethod === 'email') {
      const email = (receiptEmail || '').trim();
      if (!email) {
        showToast('전송할 이메일을 입력해주세요.', 'error');
        return;
      }
      try {
        setIsSendingReceipt(true);
        const { data } = await axios.post(
          baseUrl + '/api/cp/payment/manual/receipt-email',
          {
            paymentTransactionId: receiptPreviewData.paymentTransactionId,
            payment_transaction_id: receiptPreviewData.paymentTransactionId,
            email,
          },
          { withCredentials: true }
        );
        if (data?.code === 1000) {
          showToast(data?.msg || '이메일로 전표가 전송되었습니다.');
          closeReceipt();
        } else {
          showToast(receiptSendError(data, data?.msg || '전표 이메일 전송에 실패했습니다.'), 'error');
        }
      } catch (err) {
        showToast(receiptSendError(err, err?.response?.data?.msg || '전표 이메일 전송 중 오류가 발생했습니다.'), 'error');
      } finally {
        setIsSendingReceipt(false);
      }
    } else if (sendMethod === 'sms') {
      const phone = (receiptPhone || '').replace(/[^0-9]/g, '');
      if (!phone) {
        showToast('휴대폰 번호를 입력해주세요.', 'error');
        return;
      }
      try {
        setIsSendingReceipt(true);
        const { data } = await axios.post(
          baseUrl + '/api/cp/payment/manual/receipt-sms',
          { paymentTransactionId: receiptPreviewData.paymentTransactionId, phone },
          { withCredentials: true }
        );
        if (data?.code === 1000) {
          showToast(data?.msg || '전표 문자가 전송되었습니다.');
          closeReceipt();
        } else {
          showToast(receiptSendError(data, '관리자에게 문의해주세요.'), 'error');
        }
      } catch (err) {
        showToast(receiptSendError(err, '관리자에게 문의해주세요.'), 'error');
      } finally {
        setIsSendingReceipt(false);
      }
    } else if (sendMethod === 'kakao') {
      const phone = (receiptPhone || '').replace(/[^0-9]/g, '');
      if (!phone) {
        showToast('휴대폰 번호를 입력해주세요.', 'error');
        return;
      }
      try {
        setIsSendingReceipt(true);
        const { data } = await axios.post(
          baseUrl + '/api/cp/payment/manual/receipt-alimtalk',
          { paymentTransactionId: receiptPreviewData.paymentTransactionId, phone },
          { withCredentials: true }
        );
        if (data?.code === 1000) {
          showToast(data?.msg || '전표 알림톡을 발송했습니다.');
          closeReceipt();
        } else {
          showToast(receiptSendError(data, data?.msg || '전표 알림톡 발송에 실패했습니다.'), 'error');
        }
      } catch (err) {
        showToast(receiptSendError(err, err?.response?.data?.msg || '전표 알림톡 발송 중 오류가 발생했습니다.'), 'error');
      } finally {
        setIsSendingReceipt(false);
      }
    }
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
      <PaymentResultMonitor rows={viewMode === 'payment' ? transactions : []} refresh={() => fetchPaymentsForDate(selectedDateKey, paymentSearchKeyword, true)} />
      <Tooltip id="cp-excel-month-tooltip" place="bottom" positionStrategy="absolute" className="max-w-[300px] text-[11px] z-[9999] whitespace-pre-line" isOpen={showAllOpen || hoveredTooltipId === 'cp-excel-month'} setIsOpen={() => {}} />
      <Tooltip id="cp-excel-daily-tooltip" place="left" positionStrategy="absolute" className="max-w-[330px] text-[11px] z-[9999] whitespace-pre-line" isOpen={showAllOpen || hoveredTooltipId === 'cp-excel-daily'} setIsOpen={() => {}} />
      <Tooltip id="cp-revenue-tooltip" place="bottom" positionStrategy="absolute" className="max-w-[190px] text-[11px] z-[9999] whitespace-pre-line" isOpen={showAllOpen || hoveredTooltipId === 'cp-revenue'} setIsOpen={() => {}} />
      <Tooltip id="cp-settlement-tooltip" place="bottom" positionStrategy="absolute" className="max-w-[190px] text-[11px] z-[9999] whitespace-pre-line" isOpen={showAllOpen || hoveredTooltipId === 'cp-settlement'} setIsOpen={() => {}} />
      <Tooltip id="cp-calendar-tooltip" place="right" positionStrategy="absolute" className="max-w-[400px] text-[11px] z-[9999] whitespace-pre-line" isOpen={showAllOpen || hoveredTooltipId === 'cp-calendar'} setIsOpen={() => {}} />
      <div className="flex-1 min-h-0 py-4 px-8 overflow-auto overflow-x-auto">
        <div className="grid grid-cols-[54fr,46fr] gap-4 h-full min-h-[520px] min-w-[1500px]">
          {/* 왼쪽: 달력 + 일별 정산 요약 (헤더 52px로 오른쪽과 동일 정렬) */}
          <div className="flex flex-col min-h-0">
            <div className="bg-white rounded-2xl border border-[#e0e0e0] shadow-md flex flex-col flex-1 min-h-0 overflow-hidden">
              {/* 달력 상단 헤더 - 오른쪽 결제내역 헤더와 동일 높이(52px) */}
              <div className="h-[52px] px-4 border-b border-[#f2f4f6] flex items-center justify-between bg-[#fcfdfe] shrink-0">
                <div className="flex items-center gap-3 min-w-0 flex-1 mr-3">
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() =>
                        setActiveStartDate(dayjs(activeStartDate).subtract(1, 'month').toDate())
                      }
                      className="w-8 h-8 flex items-center justify-center text-[#191f28] hover:bg-[#f2f4f6] rounded"
                    >
                      ‹
                    </button>
                    <div className="text-[16px] font-bold text-[#191f28] min-w-[100px]">
                      {dayjs(activeStartDate).format('YYYY년 M월')}
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setActiveStartDate(dayjs(activeStartDate).add(1, 'month').toDate())
                      }
                      className="w-8 h-8 flex items-center justify-center text-[#191f28] hover:bg-[#f2f4f6] rounded"
                    >
                      ›
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  data-tooltip-id="cp-excel-month-tooltip"
                  data-tooltip-html={PHPAY_INTEGRATED_SETTLEMENT_EXPORT_ENABLED ? '해당 월의 결제, 정산, 입금 내역을 XLSX 파일로 다운로드합니다.<br/>각 내역은 별도 시트로 구성됩니다.' : '해당 월의 매출, 정산, 입금내역을 다운로드합니다.<br/>지급일 컬럼이 포함됩니다.'}
                  onMouseEnter={() => !showAllOpen && setHoveredTooltipId('cp-excel-month')}
                  onMouseLeave={() => setHoveredTooltipId(null)}
                  onClick={handleExcelDownload}
                  disabled={excelDownloading}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#191f28] text-white rounded-lg font-bold text-[13px] hover:bg-[#333d48] disabled:opacity-60 disabled:cursor-not-allowed shrink-0 cursor-help"
                >
                  <Download size={14} />
                  {PHPAY_INTEGRATED_SETTLEMENT_EXPORT_ENABLED ? '통합정산 엑셀다운로드' : '월간 매출, 정산, 입금 엑셀다운로드'}
                </button>
              </div>
              <div className="flex-1 min-h-0 overflow-auto p-4">
                <Calendar
                  locale="ko-KR"
                  calendarType="gregory"
                  value={selectedDate}
                  onChange={(date) => {
                    const clickedDate = dayjs(date);
                    const currentMonth = dayjs(activeStartDate);

                    // 이웃 달(전달/다음달) 날짜를 클릭하면 해당 달로 이동
                    if (
                      clickedDate.year() !== currentMonth.year() ||
                      clickedDate.month() !== currentMonth.month()
                    ) {
                      setActiveStartDate(clickedDate.startOf('month').toDate());
                    }

                    setSelectedDate(date);
                    setViewMode('deposit');
                  }}
                  activeStartDate={activeStartDate}
                  onActiveStartDateChange={({ activeStartDate: next }) =>
                    setActiveStartDate(next)
                  }
                  showNeighboringMonth={true}
                  // 6줄 강제는 사용하지 않고 react-calendar 기본 로직 사용
                  formatDay={(_, date) => dayjs(date).date().toString()}
                  prevLabel={<span className="text-[#191f28]">‹</span>}
                  nextLabel={<span className="text-[#191f28]">›</span>}
                  prev2Label={null}
                  next2Label={null}
                  navigationLabel={({ date, label, locale, view }) => {
                    return '';
                  }}
                  tileContent={({ date, view }) => {
                    if (view !== 'month') return null;
                    const key = dayjs(date).format('YYYY-MM-DD');
                    const row = summaryMap[key];
                    const pay = Number(row?.paymentAmount || 0);
                    const cancel = Number(row?.cancelAmount || 0);
                    const rev = pay - cancel;
                    const settle = getSettlementNetAmount(row);
                    const deposit = getDepositAmount(row);
                    const isCurrentMonth =
                      dayjs(date).year() === dayjs(activeStartDate).year() &&
                      dayjs(date).month() === dayjs(activeStartDate).month();
                    const opacityClass = isCurrentMonth ? '' : 'opacity-40';
                    const isSelected = dayjs(date).format('YYYY-MM-DD') === dayjs(selectedDate).format('YYYY-MM-DD');
                    const rowClass = 'flex justify-between items-center gap-1 w-full';
                    const labelClass = 'text-[11px] text-[#8b95a1] font-semibold shrink-0';
                    const amountClass = 'text-[12px] font-medium tabular-nums shrink-0';
                    return (
                      <div className={`w-full text-[12px] ${opacityClass}`}>
                        {isSelected ? (
                          <>

                            <div
                              role="button"
                              tabIndex={0}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedDate(date);
                                setViewMode('payment');
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setSelectedDate(date);
                                  setViewMode('payment');
                                }
                              }}
                              className={`w-full text-left rounded px-1.5 py-0.5 cursor-pointer hover:opacity-90 flex justify-between items-center ${viewMode === 'payment'
                                  ? 'bg-[#191f28] text-white'
                                  : 'bg-transparent text-[#191f28] hover:bg-[var(--brand-primary-soft)]'
                                }`}
                            >
                              <span className={viewMode === 'payment' ? 'text-white/90' : labelClass}>매출</span>
                              <span className={`${amountClass} shrink-0`}>{rev.toLocaleString('ko-KR')}</span>
                            </div>
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedDate(date);
                                setViewMode('settlement');
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setSelectedDate(date);
                                  setViewMode('settlement');
                                }
                              }}
                              className={`w-full text-left rounded px-1.5 py-0.5 cursor-pointer hover:opacity-90 flex justify-between items-center ${viewMode === 'settlement'
                                  ? 'bg-[var(--brand-primary)] text-white'
                                  : 'bg-transparent text-[var(--brand-primary)] hover:bg-[var(--brand-primary-soft)]'
                                }`}
                            >
                              <span className={viewMode === 'settlement' ? 'text-white/90' : labelClass}>정산</span>
                              <span className={`${amountClass} shrink-0`}>{settle.toLocaleString('ko-KR')}</span>
                            </div>
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedDate(date);
                                setViewMode('deposit');
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setSelectedDate(date);
                                  setViewMode('deposit');
                                }
                              }}
                              className={`w-full text-left rounded px-1.5 py-0.5 cursor-pointer hover:opacity-90 flex justify-between items-center ${viewMode === 'deposit'
                                  ? 'bg-[#16a34a] text-white'
                                  : 'bg-transparent text-[#16a34a] hover:bg-[#ecfdf3]'
                                }`}
                            >
                              <span className={viewMode === 'deposit' ? 'text-white/90' : labelClass}>입금</span>
                              <span className={`${amountClass} shrink-0`}>{deposit.toLocaleString('ko-KR')}</span>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className={rowClass}>
                              <span className={labelClass}>매출</span>
                              <span className={`${amountClass} text-[#191f28]`}>
                                {rev.toLocaleString('ko-KR')}
                              </span>
                            </div>
                            <div className={rowClass}>
                              <span className={labelClass}>정산</span>
                              <span className={`${amountClass} text-[var(--brand-primary)]`}>
                                {settle.toLocaleString('ko-KR')}
                              </span>
                            </div>
                            <div className={rowClass}>
                              <span className={labelClass}>입금</span>
                              <span className={`${amountClass} text-[#16a34a]`}>
                                {deposit.toLocaleString('ko-KR')}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  }}
                  tileClassName={({ date, view }) => {
                    if (view !== 'month') return '';
                    const isCurrentMonth =
                      dayjs(date).year() === dayjs(activeStartDate).year() &&
                      dayjs(date).month() === dayjs(activeStartDate).month();
                    const dayOfWeek = date.getDay();
                    const isWeekend =
                      isCurrentMonth && (dayOfWeek === 0 || dayOfWeek === 6);
                    return isWeekend ? ' text-red-500' : '';
                  }}
                  className="w-full border-none text-[14px] react-calendar-custom integrated-settlement-calendar"
                />
              </div>
              <div className="relative px-4 py-2 border-t border-[#f2f4f6] shrink-0">
               <div className="flex items-center justify-between gap-3">
                <span
                  data-tooltip-id="cp-calendar-tooltip"
                  data-tooltip-html="선택한 월의 일별 매출/정산/입금 금액을 확인할 수 있습니다.<br/>원하시는 날짜를 선택하면, 선택한 날의 세부내역을 확인할 수 있습니다.<br/>매출은 승인(취소금액 상계) 합계 금액입니다."
                  onMouseEnter={() => !showAllOpen && setHoveredTooltipId('cp-calendar')}
                  onMouseLeave={() => setHoveredTooltipId(null)}
                  className="inline-flex items-center gap-1 text-[12px] text-[#8b95a1] cursor-help"
                >
                  <HelpCircle size={14} className="text-[#8b95a1]" />
                  달력 안내
                </span>
                <div className="flex items-center gap-2 text-[13px] font-bold tabular-nums text-[#191f28] whitespace-nowrap">
                  <span>매출 {monthlyTotals.revenue.toLocaleString('ko-KR')}원</span>
                  <span className="text-[#c0c4cc]">|</span>
                  <span className="text-[var(--brand-primary)]">정산 {monthlyTotals.settlement.toLocaleString('ko-KR')}원</span>
                  <span className="text-[#c0c4cc]">|</span>
                  <span className="text-[#16a34a]">입금 {monthlyTotals.deposit.toLocaleString('ko-KR')}원</span>
                </div>
                {contractAvailability.status === 'available' && <BreakdownToggle label="월간 합계" expanded={showMonthlyContracts} onClick={() => setShowMonthlyContracts((value) => !value)} />}
                {contractAvailability.status === 'error' && <button type="button" onClick={contractAvailability.retry} className="text-[12px] text-[#b45309] underline">상세 기능 확인 실패 · 다시 확인</button>}
               </div>
               {contractAvailability.status === 'available' && showMonthlyContracts && (
                 <div className="absolute bottom-full left-3 right-3 z-20 rounded-t-lg border border-[#dbe4ed] bg-white px-3 py-2 shadow-lg">
                   <ContractBreakdown period="monthly" periodKey={activeMonthKey} onAuthError={handleContractAuthError} />
                 </div>
               )}
              </div>
            </div>

            {/* 일별 정산 요약 (주석 처리) */}
            {/* <div className="bg-white rounded-2xl border border-[#e0e0e0] shadow-md p-5 flex flex-col gap-4">
              <div className="flex items-center gap-2 shrink-0">
                <div className="w-10 h-10 bg-[#f2f4f6] rounded-lg flex items-center justify-center shrink-0">
                  <Banknote size={20} className="text-[var(--brand-primary)]" />
                </div>
                <div className="flex flex-col">
                  <h2 className="text-[18px] font-extrabold text-[#191f28]">
                    일별 정산 요약
                  </h2>
                  <span className="text-[13px] text-[#8b95a1]">
                    {dayjs(selectedDate).format('YYYY.MM.DD (ddd)')}
                  </span>
                </div>
              </div>

              {daySummary ? (
                <div className="space-y-4">
                  {hasSales && (
                    <div className="bg-[#f9fafb] rounded-xl border border-[#e5e8eb] p-4 space-y-3">
                      <div className="text-[13px] font-bold text-[#8b95a1] mb-2">
                        매출
                      </div>
                      {paymentAmount > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-[13px] text-[#8b95a1]">승인</span>
                          <span className="text-[18px] font-medium text-[#191f28]">
                            {paymentAmount.toLocaleString()}원 ({paymentCount}건)
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-[13px] text-[#8b95a1]">취소</span>
                        <span className="text-[18px] font-medium text-[#b91c1c]">
                          {cancelAmount.toLocaleString()}원 ({cancelCount}건)
                        </span>
                      </div>
                      {revenueAmount !== 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-[13px] text-[#8b95a1]">매출</span>
                          <span className="text-[18px] font-medium text-[#191f28]">
                            {revenueAmount.toLocaleString()}원
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  {hasSettlement && (
                    <div className="bg-[#f9fafb] rounded-xl border border-[#e5e8eb] p-4 space-y-3">
                      <div className="text-[13px] font-bold text-[#8b95a1] mb-2">
                        정산
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[13px] text-[#8b95a1]">정산</span>
                        <span className="text-[18px] font-bold text-[var(--brand-primary)]">
                          {settlementAmount.toLocaleString()}원 (
                          {settlementCount}건)
                        </span>
                      </div>
                    </div>
                  )}
                  {!hasSales && !hasSettlement && !hasDeposit && (
                    <div className="bg-[#f9fafb] rounded-xl border border-[#e5e8eb] p-4 text-[13px] text-[#8b95a1]">
                      해당 날짜에 매출·정산·입금 내역이 없습니다.
                    </div>
                  )}
                </div>
              ) : (
                <div className="py-6 text-center text-[14px] text-[#8b95a1]">
                  선택한 날짜의 정산 데이터가 없습니다.
                </div>
              )}
            </div> */}
          </div>

          {/* 오른쪽: 해당일 결제내역 테이블 */}
          <div className={`flex flex-col min-w-0 min-h-0 bg-white rounded-2xl shadow-md overflow-hidden overflow-x-auto ${viewMode === 'payment'
              ? 'border-2 border-[#191f28]'
              : viewMode === 'settlement'
                ? 'border-2 border-[var(--brand-primary)]'
                : viewMode === 'deposit'
                  ? 'border-2 border-[#16a34a]'
                : 'border border-[#e0e0e0]'
            }`}>
            <div className="h-[52px] px-4 border-b border-[#f2f4f6] flex items-center justify-between bg-[#fcfdfe] shrink-0">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <span className="text-[18px] font-bold text-[#191f28] shrink-0">
                  {dayjs(selectedDate).format('YY.MM.DD.')} {viewMode === 'payment' ? '결제내역' : viewMode === 'deposit' ? '입금내역' : isPendingSettlement ? '정산예정내역' : '정산내역'}
                </span>
                {viewMode === 'payment' && <PaymentStatusFilter value={resultStatusFilter} onChange={setResultStatusFilter} />}
                {viewMode === 'payment' && (
                  <div className="flex items-center gap-2 flex-1 max-w-[280px] min-w-[200px]">
                    <div className="relative flex-1">
                      <Search size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8b95a1]" />
                      <input
                        type="text"
                        placeholder="약국명 검색"
                        value={paymentSearchKeyword}
                        onChange={(e) => setPaymentSearchKeyword(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            fetchPaymentsForDate(selectedDateKey, paymentSearchKeyword);
                          }
                        }}
                        className="w-full pl-8 pr-3 py-1.5 text-[13px] border border-[#e5e7eb] rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]/30 focus:border-[var(--brand-primary)]"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => fetchPaymentsForDate(selectedDateKey, paymentSearchKeyword)}
                      className="px-3 py-1.5 bg-[#191f28] text-white rounded-lg font-bold text-[13px] hover:bg-[#333d48] transition-colors shrink-0 flex items-center gap-1.5"
                    >
                      <Search size={14} />
                      검색
                    </button>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {contractAvailability.status === 'available' && <BreakdownToggle label="일별 합계" expanded={showDailyContracts} onClick={() => setShowDailyContracts((value) => !value)} />}
                <button
                  type="button"
                  data-tooltip-id="cp-excel-daily-tooltip"
                  data-tooltip-html="선택한 날의 결제/정산/입금내역을 다운로드합니다."
                  onMouseEnter={() => !showAllOpen && setHoveredTooltipId('cp-excel-daily')}
                  onMouseLeave={() => setHoveredTooltipId(null)}
                  onClick={handleDailyExcelDownload}
                  disabled={dailyExcelDownloading || displayedTransactions.length === 0}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#191f28] text-white rounded-lg font-bold text-[13px] hover:bg-[#333d48] disabled:opacity-60 disabled:cursor-not-allowed shrink-0 cursor-help"
                >
                  <Download size={14} />
                  엑셀다운로드
                </button>
              </div>
            </div>
            {recoveryView && viewMode === 'payment' && <div role="status" className="shrink-0 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-900">결제 결과를 확인해 주세요. 실패로 표시되어도 다시 결제하지 말고 관리자에게 확인해 주세요.</div>}
            {/* 표 위 합산: 결제내역/정산내역 동일한 레이아웃 (결제일 제외) */}
            {showDailyContracts && contractAvailability.status === 'available' ? (
              <div className="shrink-0 border-b-[5px] border-[#e5e8eb] bg-[#f0f4f8] px-4 py-2"><ContractBreakdown period="daily" periodKey={selectedDateKey} onAuthError={handleContractAuthError} /></div>
            ) : <div className="shrink-0 border-b-[5px] border-[#e5e8eb] bg-[#f0f4f8] flex text-[13px]">
              <div className="flex-[2] flex items-center justify-between gap-2 min-w-0 px-4 py-2 border-r border-[#e5e8eb]">
                <span className="text-[#8b95a1] font-bold shrink-0">건수</span>
                <span className="text-[#191f28] font-bold text-[16px] tabular-nums">
                  {totalCount}건
                </span>
              </div>
              <div className="flex-[3] flex items-center justify-between gap-2 min-w-0 px-4 py-2 border-r border-[#e5e8eb]">
                <span className="text-[#8b95a1] font-bold shrink-0">결제금액</span>
                <span className="text-[#191f28] font-bold text-[16px] tabular-nums">
                  {totalAmount.toLocaleString('ko-KR')}원
                </span>
              </div>
              <div className="flex-[3] flex items-center justify-between gap-2 min-w-0 px-4 py-2 border-r border-[#e5e8eb]">
                <span className="text-[#8b95a1] font-bold shrink-0">수수료</span>
                <span className="text-[#191f28] font-bold text-[16px] tabular-nums">
                  {totalFee.toLocaleString('ko-KR')}원
                </span>
              </div>
              <div
                className="flex-[3] flex items-center justify-between gap-2 min-w-0 px-4 py-2 border-r-0"
              >
                <span className="text-[#8b95a1] font-bold shrink-0">
                  {viewMode === 'payment' || isPendingSettlement ? '정산예정금액' : viewMode === 'deposit' ? '입금금액' : '정산금액'}
                </span>
                <span className="text-[var(--brand-primary)] font-bold text-[16px] tabular-nums">
                  {totalSettlement.toLocaleString('ko-KR')}원
                </span>
              </div>
            </div>}
            {/* 세부내역 펼칠 때 스크롤바 생기면서 좌우로 흔들리지 않도록 항상 세로 스크롤 표시 */}
            <div className="flex-1 min-h-0 overflow-y-scroll overflow-x-auto custom-scrollbar">
              <table className="w-full text-left text-[13px] table-fixed">
                <colgroup>
                  {viewMode === 'payment' ? (
                    <>
                      <col style={{ width: '60px' }} />
                      <col style={{ width: '75px' }} />
                      <col style={{ width: 'auto' }} />
                      <col style={{ width: '80px' }} />
                      <col style={{ width: '60px' }} />
                      <col style={{ width: '110px' }} />
                      <col style={{ width: '90px' }} />
                      <col style={{ width: '90px' }} />
                      <col style={{ width: '90px' }} />
                      <col style={{ width: '60px' }} />
                    </>
                  ) : viewMode === 'settlement' ? (
                    <>
                      <col style={{ width: '120px' }} />
                      <col style={{ width: 'auto' }} />
                      <col style={{ width: '130px' }} />
                      <col style={{ width: '110px' }} />
                      <col style={{ width: '100px' }} />
                      <col style={{ width: '110px' }} />
                      <col style={{ width: '105px' }} />
                    </>
                  ) : (
                    <>
                      <col style={{ width: '130px' }} />
                      <col style={{ width: 'auto' }} />
                      <col style={{ width: '110px' }} />
                      <col style={{ width: '100px' }} />
                      <col style={{ width: '110px' }} />
                    </>
                  )}
                </colgroup>
                <thead className="bg-[#f9fafb] text-[#8b95a1] font-bold text-[13px] sticky top-0 z-10 border-b border-[#f2f4f6]">
                  <tr>
                    {viewMode === 'payment' ? (
                      <>
                        <th className="px-2 py-2 whitespace-nowrap">결제시간</th>
                        <th className="px-2 py-2 whitespace-nowrap">승인번호</th>
                        <th className="px-2 py-2 whitespace-nowrap">약국</th>
                        <th className="px-2 py-2 whitespace-nowrap">카드사</th>
                        <th className="px-2 py-2 whitespace-nowrap">할부</th>
                        <th className="px-2 py-2 text-right whitespace-nowrap">금액</th>
                        <th className="px-2 py-2 text-right whitespace-nowrap">수수료</th>
                        <th className="px-2 py-2 whitespace-nowrap">영업자</th>
                        <th className="px-2 py-2 whitespace-nowrap">상태</th>
                        <th className="px-2 py-2 text-center whitespace-nowrap">전표</th>
                      </>
                    ) : viewMode === 'settlement' ? (
                      <>
                        <th className="pl-2 pr-2 py-2 whitespace-nowrap">결제일시</th>
                        <th className="px-2 py-2 whitespace-nowrap">약국</th>
                        <th className="px-2 py-2 whitespace-nowrap">PG사</th>
                        <th className="px-2 py-2 text-right whitespace-nowrap">금액</th>
                        <th className="px-2 py-2 text-right whitespace-nowrap">수수료</th>
                        <th className="px-2 py-2 text-right whitespace-nowrap">정산</th>
                        <th className="px-2 py-2 text-center whitespace-nowrap">상태</th>
                      </>
                    ) : (
                      <>
                        <th className="pl-2 pr-2 py-2 whitespace-nowrap">결제일시</th>
                        <th className="px-2 py-2 whitespace-nowrap">PG사</th>
                        <th className="px-2 py-2 text-right whitespace-nowrap">금액</th>
                        <th className="px-2 py-2 text-right whitespace-nowrap">수수료</th>
                        <th className="px-2 py-2 text-right whitespace-nowrap">정산</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {paymentLoading ? (
                    <tr>
                      <td
                        colSpan={viewMode === 'payment' ? 10 : viewMode === 'settlement' ? 7 : 5}
                        className="px-2 py-12 text-center text-[13px] text-[#8b95a1]"
                      >
                        불러오는 중...
                      </td>
                    </tr>
                  ) : !displayedTransactions?.length ? (
                    <tr>
                      <td
                        colSpan={viewMode === 'payment' ? 10 : viewMode === 'settlement' ? 7 : 5}
                        className="px-2 py-12 text-center text-[13px] text-[#8b95a1]"
                      >
                        해당 날짜 {viewMode === 'payment' ? '결제내역' : viewMode === 'deposit' ? '입금내역' : '정산내역'}이 없습니다.
                      </td>
                    </tr>
                  ) : (
                    displayedTransactions.map((item) => {
                      const isCancel = item.transactionType === 'CANCEL';
                      const finalStatus = String(item.status || '').toUpperCase();
                      const canCancel =
                        item.transactionType === 'APPROVAL' &&
                        finalStatus === 'COMPLETED';
                      const isCanceledApproval =
                        item.transactionType === 'APPROVAL' &&
                        finalStatus === 'CANCELED';
                      const rowBg = isCancel
                        ? ' bg-[#fef2f2]'
                        : isCanceledApproval
                          ? ' bg-[#f1f3f5]'
                          : '';
                      const amount = Number(item.amount || 0);
                      const displayedAmount = isCancel
                        ? -Math.abs(amount)
                        : amount;
                      const fee = Number(item.fee ?? 0);
                      const itemSettlement = Number(item.settlement);
                      const settlement = Number.isFinite(itemSettlement)
                        ? itemSettlement
                        : amount - fee;
                      const transactionStatus = String(item.transactionStatus || '').toUpperCase();
                      const statusBadgeClass = isUnknownPayment(item) ? 'bg-amber-50 text-amber-700' : transactionStatus === 'FAILED'
                        ? 'bg-[#fee2e2] text-[#b91c1c]'
                        : transactionStatus === 'REQUESTED' || transactionStatus === 'APPROVED'
                          ? 'bg-[#fff7ed] text-[#c2410c]'
                          : 'bg-[#e8f3ff] text-[#1d4ed8]';
                      return (
                        <tr key={item.id} className={`border-b border-[#eaeaea] hover:bg-[#f9fafb] transition-all${rowBg}`}>
                          {viewMode === 'payment' ? (
                            <>
                              <td className={`px-2 py-2 text-[#4e5968] whitespace-nowrap${rowBg}`}>
                                {formatKST(item.date, 'HH:mm')}
                              </td>
                              <td className={`px-2 py-2 text-[#4e5968] font-mono whitespace-nowrap${rowBg}`}>
                                {item.approvalNo || '-'}
                              </td>
                              <td className={`px-2 py-2 text-[#191f28] font-normal whitespace-nowrap${rowBg}`} title={item.pharmacy || ''}>
                                <span className="block w-full overflow-hidden text-ellipsis whitespace-nowrap">
                                  {item.pharmacy || '-'}
                                </span>
                                {showDailyContracts && contractAvailability.status === 'available' && item.contractDisplayLabel != null && <span className="text-[11px] text-[#6b7684]">{item.contractDisplayLabel}</span>}
                              </td>
                              <td className={`px-2 py-2 text-[#4e5968] whitespace-nowrap${rowBg}`} title={item.cardName || ''}>
                                <span className="block w-full overflow-hidden text-ellipsis whitespace-nowrap">
                                  {item.cardName || '-'}
                                </span>
                              </td>
                              <td className={`px-2 py-2 text-[#4e5968] whitespace-nowrap${rowBg}`}>
                                {item.type || '-'}
                              </td>
                              <td className={`px-2 py-2 text-right tabular-nums whitespace-nowrap${rowBg} ${isCancel ? 'font-semibold text-[#b91c1c]' : 'text-[#191f28]'}`}>
                                {displayedAmount.toLocaleString('ko-KR')}원
                              </td>
                              <td className={`px-2 py-2 text-right text-[#4e5968] tabular-nums whitespace-nowrap${rowBg}`}>
                                {fee.toLocaleString('ko-KR')}원
                              </td>
                              <td className={`px-2 py-2 text-[#4e5968] whitespace-nowrap${rowBg}`} title={item.driver || ''}>
                                <span className="block w-full overflow-hidden text-ellipsis whitespace-nowrap">
                                  {item.driver || '-'}
                                </span>
                              </td>
                              <td className={`px-2 py-2 whitespace-nowrap${rowBg}`}>
                                {isUnknownPayment(item) ? <span title={safePaymentResultMessage(item)} className="rounded bg-amber-50 px-2 py-1 text-amber-700">{unknownPaymentLabel(item)}</span> : isCancel ? (
                                  <span className="inline-flex rounded bg-[#fee2e2] px-1.5 py-0.5 font-semibold text-[#b91c1c]">
                                    취소
                                  </span>
                                ) : isCanceledApproval ? (
                                  <>
                                    <span
                                      className="inline-flex items-baseline rounded bg-[#dee2e6] px-1.5 py-0.5 font-semibold text-[#4e5968]"
                                      title={item.canceledAt
                                        ? `이 승인 건은 ${formatKST(item.canceledAt, 'YYYY.MM.DD')}에 취소되었습니다.`
                                        : '이 승인 건은 이후 취소되었습니다.'}
                                    >
                                      취소
                                      {item.canceledAt && (
                                        <span className="ml-1 text-[10px] font-medium text-[#6b7280]">
                                          {formatKST(item.canceledAt, 'MM/DD')}
                                        </span>
                                      )}
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <span className={`inline-flex rounded px-1.5 py-0.5 font-semibold ${statusBadgeClass}`}>
                                      {getStatusLabelFromTx(item)}
                                    </span>
                                    {canCancel && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedItem(item);
                                          setCancelModalOpen(true);
                                        }}
                                        className="ml-2 px-2 py-0.5 rounded text-[12px] font-semibold text-[#b91c1c] border border-[#fecaca] bg-[#fef2f2] hover:bg-[#fee2e2] shrink-0"
                                      >
                                        취소
                                      </button>
                                    )}
                                  </>
                                )}
                              </td>
                              <td className={`px-2 py-2 text-center whitespace-nowrap${rowBg}`}>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openReceiptPreview(item);
                                  }}
                                  className="px-2 py-0.5 rounded text-[12px] font-semibold text-[var(--brand-primary)] border border-[var(--brand-primary)] bg-white hover:bg-[var(--brand-primary-soft)] shrink-0"
                                >
                                  전표
                                </button>
                              </td>
                            </>
                          ) : viewMode === 'settlement' ? (
                            <>
                              <td className={`pl-2 pr-2 py-2 text-[#4e5968] whitespace-nowrap${rowBg}`}>
                                {formatKST(item.date, 'YYYY.MM.DD HH:mm')}
                              </td>
                              <td className={`px-2 py-2 text-[#191f28] font-normal whitespace-nowrap${rowBg}`} title={item.pharmacy || ''}>
                                {item.pharmacy || '-'}
                              </td>
                              <td className={`px-2 py-2 text-[#4e5968] whitespace-nowrap${rowBg}`}>
                                {getPgName(item.pgProvider)}
                              </td>
                              <td className={`px-2 py-2 text-right tabular-nums whitespace-nowrap${rowBg} ${amount < 0 ? 'font-semibold text-[#b91c1c]' : 'text-[#191f28]'}`}>
                                {amount.toLocaleString('ko-KR')}원
                              </td>
                              <td className={`px-2 py-2 text-right tabular-nums whitespace-nowrap${rowBg} ${fee < 0 ? 'font-semibold text-[var(--brand-primary)]' : 'text-[#4e5968]'}`}>
                                {fee.toLocaleString('ko-KR')}원
                              </td>
                              <td className={`px-2 py-2 text-right tabular-nums whitespace-nowrap${rowBg} ${settlement < 0 ? 'font-semibold text-[#b91c1c]' : 'text-[#191f28]'}`}>
                                {settlement.toLocaleString('ko-KR')}원
                              </td>
                              <td className={`px-2 py-2 text-center whitespace-nowrap${rowBg}`}>
                                <span
                                  title={item.settlementStageLabel || ''}
                                  className={`inline-flex rounded px-1.5 py-0.5 text-[12px] font-semibold ${
                                    item.settlementLineStatus === '취소 반영'
                                      ? 'bg-[#fee2e2] text-[#b91c1c]'
                                      : item.settlementLineStatus === '후일 취소'
                                        ? 'bg-[#fff7ed] text-[#c2410c]'
                                        : item.settlementLineStatus === '취소 원승인' || item.settlementLineStatus === '이월·조정'
                                          ? 'bg-[#e5e7eb] text-[#4e5968]'
                                          : 'bg-[#e8f3ff] text-[#1d4ed8]'
                                  }`}
                                >
                                  {item.settlementLineStatus || item.settlementStageLabel || '-'}
                                </span>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className={`pl-2 pr-2 py-2 text-[#4e5968] whitespace-nowrap${rowBg}`}>
                                {formatKST(item.date, 'YYYY.MM.DD HH:mm')}
                              </td>
                              <td className={`px-2 py-2 text-[#4e5968] whitespace-nowrap${rowBg}`}>
                                {getPgName(item.pgProvider)}
                              </td>
                              <td className={`px-2 py-2 text-right text-[#191f28] tabular-nums whitespace-nowrap${rowBg}`}>
                                {amount.toLocaleString('ko-KR')}원
                              </td>
                              <td className={`px-2 py-2 text-right text-[#4e5968] tabular-nums whitespace-nowrap${rowBg}`}>
                                {fee.toLocaleString('ko-KR')}원
                              </td>
                              <td className={`px-2 py-2 text-right text-[#191f28] tabular-nums whitespace-nowrap${rowBg}`}>
                                {settlement.toLocaleString('ko-KR')}원
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* 취소 확인 모달 */}
      {cancelModalOpen && selectedItem && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[1000]">
          <div className="bg-white rounded-2xl px-6 py-5 w-full max-w-[360px] shadow-xl">
            <div className="text-[18px] font-bold text-[#111827] mb-2">
              결제를 취소하시겠습니까?
            </div>
            <div className="text-[14px] text-[#6b7280] mb-5 leading-relaxed">
              선택한 거래(
              <span className="font-semibold">{selectedItem.pharmacy}</span>,{' '}
              <span className="font-semibold">
                {Number(selectedItem.amount || 0).toLocaleString('ko-KR')}원
              </span>
              )를 취소 처리합니다.
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setCancelModalOpen(false)}
                className="flex-1 px-3 py-2.5 rounded-xl text-[14px] font-semibold bg-[#e5e7eb] text-[#111827]"
              >
                닫기
              </button>
              <button
                type="button"
                onClick={handleConfirmCancel}
                className="flex-1 px-3 py-2.5 rounded-xl text-[14px] font-semibold bg-[#ef4444] text-white"
              >
                취소하기
              </button>
            </div>
          </div>
        </div>
      )}

      {receiptModalOpen && receiptPreviewData && RECEIPT_UI_VERSION === 2 && (
        <ReceiptPreviewModalV2
          data={receiptPreviewData}
          viewMode={receiptViewMode}
          onViewModeChange={setReceiptViewMode}
          onRevealCardNumber={() => setRevealModalOpen(true)}
          sendMethod={sendMethod}
          onSendMethodChange={setSendMethod}
          email={receiptEmail}
          onEmailChange={(value) => { receiptEmailLookup.markEdited(); setReceiptEmail(value); }}
          emailStatusContent={<CurrentReceiptEmailStatus status={receiptEmailLookup.status} retry={receiptEmailLookup.retry} />}
          phone={receiptPhone}
          onPhoneChange={setReceiptPhone}
          onSend={handleConfirmSendReceipt}
          onClose={() => {
            if (isSendingReceipt) return;
            receiptInfoRequestRef.current += 1;
            setReceiptModalOpen(false);
            setReceiptEmail('');
            setReceiptPhone('');
            setSendMethod('email');
            setReceiptPreviewData(null);
            setReceiptViewMode('approval');
          }}
          isSending={isSendingReceipt}
          smsEnabled={RECEIPT_SMS_ENABLED}
          kakaoEnabled={RECEIPT_KAKAO_ENABLED}
        />
      )}
      {/* 영수증 미리보기 모달 (UI 1) */}
      {receiptModalOpen && receiptPreviewData && RECEIPT_UI_VERSION !== 2 && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[1000] overflow-y-auto py-8" onClick={() => { if (isSendingReceipt) return; receiptInfoRequestRef.current += 1; setReceiptModalOpen(false); setReceiptEmail(''); setReceiptPhone(''); setSendMethod('email'); setReceiptPreviewData(null); setReceiptViewMode('approval'); }}>
          <div className="bg-white rounded border border-[#e0e0e0] w-[360px] shadow-xl my-auto flex flex-col max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white z-10 px-5 pt-4 pb-4 border-b border-[#333333] flex items-center justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="text-[18px] font-bold text-[#111111] leading-tight">{RECEIPT_LABELS.titleKo}</div>
                <div className="mt-0.5 text-[11px] font-semibold leading-tight text-[#767676]">{RECEIPT_LABELS.titleEn}</div>
              </div>
              {receiptPreviewData.isCanceled && (
                <div className="flex rounded overflow-hidden border border-[#e0e0e0] shrink-0">
                  <button
                    type="button"
                    onClick={() => setReceiptViewMode('approval')}
                    className={`px-3 py-1.5 text-[13px] font-semibold ${receiptViewMode === 'approval'
                        ? 'bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]'
                        : 'bg-white text-[var(--brand-primary)] border border-transparent hover:bg-[var(--brand-primary-soft)]'
                      }`}
                  >
                    승인
                  </button>
                  <button
                    type="button"
                    onClick={() => setReceiptViewMode('cancel')}
                    className={`px-3 py-1.5 text-[13px] font-semibold ${receiptViewMode === 'cancel'
                        ? 'bg-[#b91c1c] text-white border-[#b91c1c]'
                        : 'bg-white text-[#b91c1c] border border-transparent hover:bg-[#fef2f2]'
                      }`}
                  >
                    취소
                  </button>
                </div>
              )}
            </div>

            <div className="overflow-y-auto flex-1 px-5 py-5 min-w-0">
              <div className="mb-0">
                <div className="text-[16px] font-bold text-[#111111] pb-3">거래정보</div>
                <div className="text-[14px]">
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">카드종류</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.cardBrand}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">카드번호</span>
                    <div className="flex items-center gap-2 ml-auto min-w-0">
                      <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.cardNumberMasked}</span>
                      <button type="button" onClick={() => setRevealModalOpen(true)} className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-[#d1d5db] text-[#4b5563] hover:bg-[#f3f4f6] shrink-0">
                        <Eye size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">할부</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.installmentMonth}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">거래유형</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptViewMode === 'approval' ? '승인' : '취소'}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">승인번호</span>
                    <span className="text-[#111111] font-medium text-right min-w-0 tabular-nums">{receiptPreviewData.approvalNumber}</span>
                  </div>
                  <div className="flex justify-between items-start py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px] pt-0.5">{receiptViewMode === 'approval' ? '거래일시' : '취소일시'}</span>
                    <span className="text-[#111111] font-medium text-right min-w-0 leading-snug">{receiptViewMode === 'approval' ? receiptPreviewData.approvalDateTime : (receiptPreviewData.cancelDateTime ?? '-')}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">거래처명</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.pharmacyName}</span>
                  </div>
                </div>
              </div>
              <div className="border-b border-[#e0e0e0] my-3.5" />

              <div>
                <div className="text-[16px] font-bold text-[#111111] pb-3">공급대가</div>
                <div className="text-[14px]">
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">과세금액</span>
                    <span className="text-[#111111] font-bold text-right min-w-0 tabular-nums">{receiptPreviewData.taxableAmount}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">부가세</span>
                    <span className="text-[#111111] font-bold text-right min-w-0 tabular-nums">{receiptPreviewData.vatAmount}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">면세금액</span>
                    <span className="text-[#111111] font-bold text-right min-w-0 tabular-nums">{receiptPreviewData.taxFreeAmount}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">봉사료</span>
                    <span className="text-[#111111] font-bold text-right min-w-0 tabular-nums">{receiptPreviewData.serviceAmount}</span>
                  </div>
                  <div className="flex justify-between items-center py-2.5">
                    <span className="text-[#111111] font-bold text-[15px] shrink-0 w-[120px]">총 결제금액</span>
                    <span className="text-[#f04438] font-bold text-[16px] text-right min-w-0 tabular-nums">{receiptPreviewData.totalAmount}</span>
                  </div>
                </div>
              </div>
              <div className="border-b border-[#e0e0e0] my-3.5" />

              <div>
                <div className="text-[16px] font-bold text-[#111111] pb-3">{RECEIPT_LABELS.storeInfo}</div>
                <div className="text-[14px]">
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">상호명</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.merchantName}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">대표자명</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.merchantOwnerName}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">사업자등록번호</span>
                    <span className="text-[#111111] font-medium text-right min-w-0 tabular-nums">{receiptPreviewData.merchantBusinessNo}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">이용문의</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.merchantContact}</span>
                  </div>
                  <div className="flex justify-between items-start py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px] pt-0.5">사업장소재지</span>
                    <span className="text-[#111111] font-medium text-right min-w-0 break-words leading-snug">{receiptPreviewData.merchantAddress}</span>
                  </div>
                </div>
              </div>
              <div className="border-b border-[#e0e0e0] my-3.5" />

              <div>
                <div className="text-[16px] font-bold text-[#111111] pb-3">{RECEIPT_LABELS.pspInfo}</div>
                <div className="text-[14px]">
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">상호명</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.supplierName}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">대표자명</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.supplierOwnerName}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">사업자등록번호</span>
                    <span className="text-[#111111] font-medium text-right min-w-0 tabular-nums">{receiptPreviewData.supplierBusinessNo}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">이용문의</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.supplierContact}</span>
                  </div>
                  <div className="flex justify-between items-start py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px] pt-0.5">사업장소재지</span>
                    <span className="text-[#111111] font-medium text-right min-w-0 break-words leading-snug">{receiptPreviewData.supplierAddress}</span>
                  </div>
                </div>
              </div>
              <div className="border-b border-[#e0e0e0] my-3.5" />

              <div className="mb-5">
                <div className="text-[16px] font-bold text-[#111111] pb-3">{RECEIPT_LABELS.pgInfo}</div>
                <div className="text-[14px]">
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">가맹점명</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.cardMerchantName}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">대표자명</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.cardMerchantOwnerName}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">사업자등록번호</span>
                    <span className="text-[#111111] font-medium text-right min-w-0 tabular-nums">{receiptPreviewData.cardMerchantBusinessNo}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">이용문의</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.cardMerchantContact}</span>
                  </div>
                  <div className="flex justify-between items-start py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px] pt-0.5">사업장소재지</span>
                    <span className="text-[#111111] font-medium text-right min-w-0 break-words leading-snug">{receiptPreviewData.cardMerchantAddress}</span>
                  </div>
                </div>
              </div>
              <div className="border-b border-[#e0e0e0] my-3.5" />
              <div className="space-y-2 text-[11px] leading-relaxed text-[#999999]">
                <div>본 전표는 이메일 전송용으로 구성된 내역이며, 결제/정산 시스템의 원장과 차이가 있을 수 있습니다.</div>
                <div className="text-[#666666]">{RECEIPT_CARD_SMS_NOTICE}</div>
              </div>
            </div>

            <div className="border-t border-[#eeeeee] p-5 bg-[#fafafa] shrink-0">
              <ReceiptImageActions data={receiptPreviewData} viewMode={receiptViewMode} />
              {/* 전송 방법 선택 */}
              <div className="flex rounded-lg overflow-hidden border border-[#e5e7eb] mb-3">
                <button type="button" onClick={() => setSendMethod('email')} className={`flex-1 py-2 text-[13px] font-semibold ${sendMethod === 'email' ? 'bg-[var(--brand-primary)] text-white' : 'bg-white text-[#4e5968] hover:bg-[#f2f4f6]'}`}>이메일</button>
                <button type="button" onClick={() => { if (RECEIPT_SMS_ENABLED) setSendMethod('sms'); }} className={`flex-1 py-2 text-[13px] font-semibold border-x border-[#e5e7eb] ${!RECEIPT_SMS_ENABLED ? 'cursor-not-allowed text-[#adb5bd]' : sendMethod === 'sms' ? 'bg-[#00b493] text-white' : 'bg-white text-[#4e5968] hover:bg-[#f2f4f6]'}`}>{RECEIPT_SMS_ENABLED ? '문자' : '문자(준비중)'}</button>
                <button type="button" onClick={() => { if (RECEIPT_KAKAO_ENABLED) setSendMethod('kakao'); }} className={`flex-1 py-2 text-[13px] font-semibold ${!RECEIPT_KAKAO_ENABLED ? 'cursor-not-allowed text-[#adb5bd]' : sendMethod === 'kakao' ? 'bg-[#fee500] text-[#3c1e1e]' : 'bg-white text-[#4e5968] hover:bg-[#f2f4f6]'}`}>{RECEIPT_KAKAO_ENABLED ? '카카오톡' : '카카오톡(준비중)'}</button>
              </div>
              {sendMethod === 'email' && (
                <>
                  <label className="block text-[13px] font-semibold text-[#4e5968] mb-1.5">이메일 주소</label>
                  <input
                    type="email"
                    value={receiptEmail}
                    onChange={(e) => { receiptEmailLookup.markEdited(); setReceiptEmail(e.target.value); }}
                    autoComplete="off"
                    placeholder="example@email.com"
                    onKeyDown={(e) => { if (e.key === 'Enter' && !isSendingReceipt) handleConfirmSendReceipt(); }}
                    className="w-full mb-3 px-3 py-2.5 rounded-lg border border-[#d1d5db] text-[14px] outline-none focus:border-[var(--brand-primary)]"
                  />
                  <CurrentReceiptEmailStatus status={receiptEmailLookup.status} retry={receiptEmailLookup.retry} />
                </>
              )}
              {sendMethod === 'sms' && (
                <>
                  <label className="block text-[13px] font-semibold text-[#4e5968] mb-1.5">휴대폰 번호</label>
                  <input
                    type="tel"
                    value={receiptPhone}
                    onChange={(e) => setReceiptPhone(e.target.value)}
                    placeholder="01012345678"
                    onKeyDown={(e) => { if (e.key === 'Enter' && !isSendingReceipt) handleConfirmSendReceipt(); }}
                    className="w-full mb-3 px-3 py-2.5 rounded-lg border border-[#d1d5db] text-[14px] outline-none focus:border-[var(--brand-primary)]"
                  />
                </>
              )}
              {sendMethod === 'kakao' && (
                <>
                  <label className="block text-[13px] font-semibold text-[#4e5968] mb-1.5">휴대폰 번호</label>
                  <input type="tel" value={receiptPhone} onChange={(e) => setReceiptPhone(e.target.value)} placeholder="01012345678" onKeyDown={(e) => { if (e.key === 'Enter' && !isSendingReceipt) handleConfirmSendReceipt(); }} className="w-full mb-3 px-3 py-2.5 rounded-lg border border-[#d1d5db] text-[14px] outline-none focus:border-[var(--brand-primary)]" />
                </>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (isSendingReceipt) return;
                    receiptInfoRequestRef.current += 1;
                    setReceiptModalOpen(false);
                    setReceiptEmail('');
                    setReceiptPhone('');
                    setSendMethod('email');
                    setReceiptPreviewData(null);
                    setReceiptViewMode('approval');
                  }}
                  className="flex-1 px-3 py-2.5 rounded-lg text-[14px] font-semibold bg-[#e5e7eb] text-[#111827] hover:bg-[#d1d5db]"
                >
                  닫기
                </button>
                <button
                  type="button"
                  onClick={handleConfirmSendReceipt}
                  disabled={isSendingReceipt || (sendMethod === 'sms' && !RECEIPT_SMS_ENABLED) || (sendMethod === 'kakao' && !RECEIPT_KAKAO_ENABLED)}
                  className={`flex-1 px-3 py-2.5 rounded-lg text-[14px] font-semibold ${(sendMethod === 'sms' && !RECEIPT_SMS_ENABLED) || (sendMethod === 'kakao' && !RECEIPT_KAKAO_ENABLED) ? 'bg-[#e5e7eb] text-[#9ca3af] cursor-not-allowed' : sendMethod === 'kakao' ? 'bg-[#fee500] text-[#3c1e1e] hover:bg-[#f0d900]' : isSendingReceipt ? 'bg-[var(--brand-primary-disabled)] text-white cursor-not-allowed' : sendMethod === 'sms' ? 'bg-[#00b493] text-white hover:bg-[#009a7f]' : 'bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary-hover)]'}`}
                >
                  {isSendingReceipt ? '전송중...' : (sendMethod === 'sms' && !RECEIPT_SMS_ENABLED) || (sendMethod === 'kakao' && !RECEIPT_KAKAO_ENABLED) ? '준비중' : sendMethod === 'kakao' ? '알림톡 전송' : sendMethod === 'sms' ? '문자 전송' : '이메일 전송'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 카드번호 조회 모달 */}
      {revealModalOpen && receiptPreviewData && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-[#e5e7eb] flex items-center justify-between">
              <h2 className="text-[16px] font-bold text-[#111827]">카드번호 조회</h2>
              <button
                type="button"
                onClick={() => {
                  if (revealLoading) return;
                  setRevealModalOpen(false);
                }}
                className="text-[#9ca3af] hover:text-[#111827] text-[18px] leading-none px-1"
              >
                ×
              </button>
            </div>
            <div className="px-5 py-4 space-y-3 text-[13px] text-[#374151]">
              <p className="text-[13px] text-[#4b5563]">
                카드번호는 개인정보 보호를 위해 기본적으로 완전 마스킹 처리됩니다.
                카드번호의 앞 6자리/뒤 4자리를 확인하려면, 아래 정보를 입력해 주세요.
              </p>
              <div className="space-y-2">
                <label className="block text-[12px] font-semibold text-[#6b7280]">
                  비밀번호
                </label>
                <input
                  type="password"
                  value={revealPassword}
                  onChange={(e) => setRevealPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-[#d1d5db] rounded-lg text-[13px] focus:outline-none focus:ring-1 focus:ring-[var(--brand-primary)]"
                  placeholder="로그인 비밀번호를 입력하세요"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-[12px] font-semibold text-[#6b7280]">
                  조회 사유
                </label>
                <input
                  type="text"
                  value={revealReason}
                  onChange={(e) => setRevealReason(e.target.value)}
                  className="w-full px-3 py-2 border border-[#d1d5db] rounded-lg text-[13px] focus:outline-none focus:ring-1 focus:ring-[var(--brand-primary)]"
                  placeholder="예: 카드사 문의, 고객 요청 등"
                />
              </div>
              <p className="text-[11px] text-[#9ca3af]">
                입력하신 정보는 카드번호 조회 내역과 함께 감사 로그로 기록될 수 있습니다.
              </p>
            </div>
            <div className="px-5 py-3 border-t border-[#e5e7eb] flex justify-end gap-2 bg-[#f9fafb]">
              <button
                type="button"
                onClick={() => {
                  if (revealLoading) return;
                  setRevealModalOpen(false);
                }}
                className="px-4 py-2 rounded-xl text-[13px] font-bold bg-white text-[#4b5563] border border-[#d1d5db]"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleRevealCardNumber}
                disabled={revealLoading}
                className="px-4 py-2 rounded-xl text-[13px] font-bold bg-[#111827] text-white disabled:opacity-60"
              >
                {revealLoading ? '조회 중...' : '조회'}
              </button>
            </div>
          </div>
        </div>
      )}
      <footer className="shrink-0 py-[0.5rem] px-[1.5rem] flex items-center justify-between text-[12px] text-[#8b95a1] border-t border-[#f2f4f6]">
        <button
          type="button"
          onClick={toggleShowAll}
          className={`inline-flex items-center gap-1.5 px-2 py-1 rounded ${showAllOpen ? 'bg-[var(--brand-primary)] text-white' : 'bg-[#e5e8eb] text-[#8b95a1] hover:bg-[#d0d7de]'}`}
          title="전체 툴팁 보기 (n)"
        >
          <HelpCircle size={14} strokeWidth={2} />
          <span>툴팁안내 (n)</span>
        </button>
        <span>{BRAND_COPYRIGHT}</span>
      </footer>
    </div>
  );
}

export default IntegratedSettlement;
